import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
  lazy,
  Suspense,
} from "react";
import {
  MessageCircle,
  CornerUpRight,
  Hash,
} from "lucide-react";
import { useChat } from "./contexts/ChatContext";
import {
  searchUsers,
  listGroupMembers,
  buildAvatarUrl,
  transcribeDictation,
} from "./services/api";
import { shellSpaces } from "./utils/shellSpaces";
import AthleticSwift from "./components/AthleticSwift";
import ConvoAvatar from "./components/ConvoAvatar";
import WorldsAtrium from "./components/WorldsAtrium";
import {
  Shell,
  ShellProvider,
  Landing,
  LandingNav,
  LandingMenu,
  ThemeToggle,
  Splash,
  Row,
  Menu,
  useResizablePanel,
  useRightEdgeClaim,
  useMobilePanels,
  useNavSheet,
  PanelBackButton,
  NavMenuButton,
  openShellSettings,
  BlockTitle,
  Tip,
  TipProvider,
  Modal,
  GridNav,
  SeamGrip,
  SplitView,
  RAIL_COLLAPSED_W,
  SlideOutPanel,
  usePanelOpen,
  formatHotkey,
  MessageCircleIcon,
  VideoIcon,
  PropertiesPlate,
  EndRail,
  NewObject,
  MessagesObject,
  BellObject,
  CallObject,
  FocusObject,
  PeopleObject,
  AttachObject,
  VoiceObject,
  FormatObject,
  EmojiObject,
  MentionObject,
  ClipObject,
  FilmObject,
  PropertiesObject,
  PinObject,
  UnreadObject,
  LeaveObject,
  TrashObject,
  useDictation,
  DictationStatus,
  setFocusMode as applyShellFocus,
  RoomArrival,
  HistorySteps,
  useHistorySteps,
} from "@ofself/shell";
import { buildSynapseCommands, SYNAPSE_HOTKEYS } from "./commands";
import SearchSpotlight from "./components/SearchSpotlight";
import Sidebar from "./components/Sidebar";
import DMsPage from "./components/DMsPage";
import GroupsPage from "./components/GroupsPage";
import ActivityPage from "./components/ActivityPage";
import FilesPage from "./components/FilesPage";
import HuddlesPage from "./components/HuddlesPage";
import DirectoriesPage, { DirectoryDetail } from "./components/DirectoriesPage";
// SettingsPage only loads when the settings panel actually opens — its own chunk.
const SettingsDetail = lazy(() =>
  import("./components/SettingsPage").then((m) => ({
    default: m.SettingsDetail,
  })),
);
import MessageList, { RecordingsSidebar } from "./components/MessageList";
import MessageInput from "./components/MessageInput";
import ThreadView from "./components/ThreadView";
import HuddleBar from "./components/HuddleBar";
import CallIsland, { useCallIslandState } from "./components/CallIsland";
import IncomingCallPanel from "./components/IncomingCallPanel";
import useModuleLayout from "./hooks/useModuleLayout";
import {
  exchangeCallback,
  getFileUrl,
  logout,
  authedFetch,
} from "./services/api";
import ChannelProperties from "./components/ChannelProperties";
import { formatTime } from "./utils/time";
import { dmDisplayName } from "./utils/convoName";
import { marked } from "marked";

const THREAD_MIN = 260;
const THREAD_MAX = 700;

const THREAD_DEFAULT = 380;
const CHAT_MIN = 360; // minimum fill-panel width; bounds how wide the thread can grow
const SIDEBAR_NOMINAL = 328; // comfortable expanded width for mobile breakpoint calc; must not read live state
const SIDEBAR_MIN = 240; // the list's seam range, the Mac's (Root.swift .resizableColumn)
const SIDEBAR_MAX = 560;

// The thread is panel N+1 — a first-class .block that's a FLAT SIBLING of the
// chat panel, opening/closing purely by its WIDTH. The kit eases that change (like
// the wireframe's openable panel): no bespoke animation, no --thread-sleeve-w hack —
// opening squeezes the chat panel (the fill). The aside stays ALWAYS mounted (see
// ThreadView), so toggling basis between width and 0 slides it in AND out; nothing
// to mount-time-trick. ThreadDock is rendered unconditionally for the same reason.

/* The conversation head's way back to the list, for the narrow-but-not-phone
   slide track only: on a phone the kit's SplitView stands its own way back,
   and two chevrons stood side by side (audit 2026-10-02). */
function HeaderBack({ show, onBack }) {
  const { isMobile: phone } = useNavSheet();
  if (!show || phone) return null;
  return <PanelBackButton className="channel-header-back" onClick={onBack} />;
}

function ThreadDock({ open, basis, separatorProps, isMobile }) {
  // The thread's resize now lives in App (lifted, so the chat panel can also carry a
  // handle for this seam). ThreadDock is just the panel: closed = basis 0 so the kit
  // eases it open AND closed.
  return (
    <ThreadView
      open={open}
      basis={open ? basis : 0}
      separatorProps={separatorProps}
      isMobile={isMobile}
    />
  );
}

// Recordings — the thread's TWIN (a huddle's recorded tracks). Identical first-class
// panel treatment: a flat .block sibling, always mounted, kit width-ease open AND
// closed, kit resize. Its own width key so it remembers a different size than the thread.
function RecordingsDock({ open, huddleId, groupId, maxWidth }) {
  // The thread's twin — same kit resize hook, its own width key.
  const { width, separatorProps } = useResizablePanel({
    edge: "left",
    initial: THREAD_DEFAULT,
    min: THREAD_MIN,
    max: maxWidth || THREAD_MAX,
    storageKey: "synapse_recordings_width",
  });
  return (
    <RecordingsSidebar
      open={open}
      basis={open ? width : 0}
      separatorProps={separatorProps}
      huddleId={huddleId}
      groupId={groupId}
    />
  );
}

function TypingIndicator() {
  const { typingUsers } = useChat();
  if (!typingUsers || typingUsers.length === 0) return null;
  const text =
    typingUsers.length === 1
      ? `${typingUsers[0]} is typing`
      : typingUsers.length === 2
        ? `${typingUsers[0]} and ${typingUsers[1]} are typing`
        : `${typingUsers[0]} and ${typingUsers.length - 1} others are typing`;
  return (
    <div className="typing-indicator">
      <span className="typing-dots">
        <span />
        <span />
        <span />
      </span>
      <span className="typing-text">{text}</span>
    </div>
  );
}

const SDK_URL = import.meta.env.VITE_SDK_URL || "http://localhost:5001";
const CLIENT_ID =
  import.meta.env.VITE_CLIENT_ID ||
  "tp_3WI15MJ-m313YzCpBwU5fa0scrAWSPUwZBivA0Dw6dU";

// ── Pre-login: the shell's <Landing> surface template ────────────────────────
// The whole landing anatomy — the bare field, the marketing rail (pinned
// expanded, Sign in in the identity slot), the centred hero + entrance
// stagger — is the SHELL's (frame/Landing). Synapse provides only what is
// Synapse's: the self-drawing mark, the strings, and the dev quick-login tray.
function LoginGate() {
  const sdkFrontend =
    import.meta.env.VITE_SDK_FRONTEND_URL || "http://localhost:3000";
  const redirectUri = `${window.location.origin}/callback`;
  const authorizeUrl = `${sdkFrontend}/authorize?client_id=${CLIENT_ID}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=read`;
  const isLocal =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";
  const [devLoading, setDevLoading] = useState(false);

  async function handleDevLogin(userId, username) {
    setDevLoading(true);
    try {
      const apiUrl = import.meta.env.VITE_CHAT_API_URL || "";
      const res = await fetch(`${apiUrl}/api/chat/auth/dev-login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId, username }),
      });
      const data = await res.json();
      if (data.token) {
        localStorage.setItem("chat_session_token", data.token);
        if (data.user)
          localStorage.setItem("chat_user", JSON.stringify(data.user));
        window.location.reload();
      }
    } catch (err) {
      console.error("Dev login failed:", err);
    }
    setDevLoading(false);
  }

  return (
    <>
    {/* Top-right hamburger → full-screen overlay, provided by the shell. Scaffolded
        now for future nav; the only item is the SYNAPSE·OFSELF wordmark, same as the
        design wireframe's menu. */}
    <LandingMenu activeId="home" variant="home" foot={<ThemeToggle />}>
      {(close) => (
        <LandingNav brand="SYNAPSE" ofself brandHref="/" activeId="home" onSelect={close} />
      )}
    </LandingMenu>
    <Landing
      currentApp="synapse"
      rail={false}
      authorizeUrl={authorizeUrl}
      mark={<AthleticSwift className="swift-mark--hero" animate />}
      wordmark="SYNAPSE"
      dev={
        isLocal && (
          <>
            <span className="landing__dev-label">Dev quick-login</span>
            <div className="landing__dev-row">
              {[
                { id: "demo-user-alice", name: "Alice" },
                { id: "demo-user-bob", name: "Bob" },
                { id: "demo-user-charlie", name: "Charlie" },
              ].map((u) => (
                <button
                  key={u.id}
                  className="landing__dev-btn"
                  disabled={devLoading}
                  onClick={() => handleDevLogin(u.id, u.name)}
                >
                  {u.name}
                </button>
              ))}
            </div>
          </>
        )
      }
    />
    </>
  );
}

function CallbackHandler() {
  const [status, setStatus] = useState("Completing sign in...");

  useEffect(() => {
    async function handleCallback() {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const userId = params.get("user_id");
      const clientId = params.get("client_id");
      const username = params.get("username");
      const sidCode = params.get("sid_code");

      if (code !== "success" || !userId || !clientId) {
        setStatus("Authorization failed. Please try again.");
        return;
      }

      try {
        const res = await exchangeCallback(userId, clientId, username, sidCode);
        localStorage.setItem("chat_session_token", res.token);
        if (res.user) {
          localStorage.setItem("chat_user", JSON.stringify(res.user));
        }
        window.location.href = "/";
      } catch (err) {
        console.error("Callback exchange failed:", err);
        setStatus("Sign in failed: " + err.message);
      }
    }

    handleCallback();
  }, []);

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        background: "var(--z0-base)",
        color: "var(--text-primary)",
        fontFamily: "var(--ofself-font-sans)",
      }}
    >
      {status}
    </div>
  );
}

function MembersTab({
  members,
  currentUser,
  createDM,
  closeModal,
  dmChannels,
  selectChannel,
  groupMembers,
  addChannelMember,
  activeChannel,
}) {
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(null);
  const colors = Array.from({ length: 10 }, (_, i) => `var(--avatar-${i})`);
  const getColor = (name) =>
    colors[
      Math.abs(
        (name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0),
      ) % colors.length
    ];

  const memberIds = new Set(members.map((m) => m.user_id));

  const filtered = search.trim()
    ? members.filter((m) =>
        (m.username || "").toLowerCase().includes(search.toLowerCase()),
      )
    : members;

  // Addable users come from the channel's parent group only — the backend
  // rejects adds for anyone outside the group with 403.
  const seenNames = new Set();
  const addable =
    search.trim().length >= 1
      ? (groupMembers || [])
          .filter((m) => {
            const uid = m.user_id || m.user?.id || m.id;
            const name = (
              m.username ||
              m.user?.username ||
              m.name ||
              ""
            ).toLowerCase();
            if (
              !uid ||
              memberIds.has(uid) ||
              uid === currentUser?.id ||
              seenNames.has(name)
            )
              return false;
            if (!name.startsWith(search.trim().toLowerCase())) return false;
            seenNames.add(name);
            return true;
          })
          .map((m) => ({
            user_id: m.user_id || m.user?.id || m.id,
            username: m.username || m.user?.username || m.name || "User",
          }))
      : [];

  async function handleAdd(user) {
    if (!activeChannel?.id) return;
    setAdding(user.user_id);
    try {
      await addChannelMember(activeChannel.id, user.user_id, user.username);
    } catch (err) {
      console.error("Failed to add member:", err);
    }
    setAdding(null);
  }

  return (
    <div className="members-tab">
      <div className="members-tab-search">
        <div className="modal-search-bar">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            className="modal-search-input"
            type="text"
            placeholder="Find or add members..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>
      <ul className="members-modal-list">
        {filtered.map((m) => {
          const name = m.username || "?";
          const isYou = m.user_id === currentUser?.id;
          return (
            <Row as="li" key={m.user_id} className="members-modal-item">
              <div
                className="members-modal-avatar"
                style={{
                  background: `color-mix(in srgb, ${hashColor(name)} 16%, transparent)`,
                  color: hashColor(name),
                }}
              >
                {name[0].toUpperCase()}
              </div>
              <span className="members-modal-name">
                {name}
                {isYou ? " (you)" : ""}
              </span>
              {!isYou && (
                <Tip label="Message">
                  <button
                    className="members-modal-dm"
                    onClick={() => {
                      const existing = (dmChannels || []).find(
                        (dm) =>
                          dm.other_user_id === m.user_id ||
                          (dm.other_username &&
                            dm.other_username.toLowerCase() ===
                              (m.username || "").toLowerCase()),
                      );
                      if (existing) {
                        selectChannel(existing);
                      } else {
                        createDM(m.user_id, { [m.user_id]: m.username });
                      }
                      closeModal();
                    }}
                  >
                    <MessageCircle size={16} strokeWidth={2} />
                  </button>
                </Tip>
              )}
            </Row>
          );
        })}
        {addable.length > 0 && (
          <>
            <li className="members-modal-divider">
              <span
                style={{
                  fontSize: "var(--ofself-text-sm)",
                  color: "var(--text-muted)",
                  textTransform: "uppercase",
                }}
              >
                Add to channel
              </span>
            </li>
            {addable.map((u) => (
              <Row as="li" key={u.user_id} className="members-modal-item">
                <div
                  className="members-modal-avatar"
                  style={{
                    background: `color-mix(in srgb, ${hashColor(u.username)} 16%, transparent)`,
                    color: hashColor(u.username),
                  }}
                >
                  {(u.username || "?")[0].toUpperCase()}
                </div>
                <span className="members-modal-name">{u.username}</span>
                <Tip label="Add to channel">
                  <button
                    className="members-modal-add"
                    onClick={() => handleAdd(u)}
                    disabled={adding === u.user_id}
                  >
                    {adding === u.user_id ? (
                      "..."
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    )}
                  </button>
                </Tip>
              </Row>
            ))}
          </>
        )}
        {filtered.length === 0 && addable.length === 0 && (
          <div
            style={{
              padding: "16px",
              color: "var(--text-muted)",
              fontSize: "var(--ofself-text-base)",
            }}
          >
            No members found
          </div>
        )}
      </ul>
    </div>
  );
}

const AVATAR_COLORS = Array.from(
  { length: 10 },
  (_, i) => `var(--avatar-${i})`,
);
function hashColor(name) {
  const idx =
    Math.abs((name || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0)) %
    AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
}

const CHAT_API_BASE = import.meta.env.VITE_CHAT_API_URL || "";

function SlashDMPicker({ query, onSelect }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      const token = localStorage.getItem("chat_session_token");
      const headers = { Authorization: `Bearer ${token}` };
      try {
        const res = await fetch(`${CHAT_API_BASE}/api/chat/plugins/discover`, {
          headers,
        });
        if (cancelled) return;
        const data = res.ok ? await res.json() : { plugins: [] };
        const plugins = (data.plugins || []).map((p) => ({
          ...p,
          key: `plugin:${p.plugin_id}`,
        }));
        setItems(plugins);
      } catch {
        if (!cancelled) setItems([]);
      }
      if (!cancelled) setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = query
    ? items.filter((it) => {
        const q = query.toLowerCase();
        return (
          (it.name || "").toLowerCase().includes(q) ||
          (it.slug || "").toLowerCase().includes(q) ||
          (it.sub_entity_key || "").toLowerCase().includes(q)
        );
      })
    : items;

  if (loading) {
    return (
      <div className="message-list-card">
        <div
          className="message-list"
          style={{ alignItems: "center", justifyContent: "center" }}
        >
          <span
            style={{
              color: "var(--text-faint)",
              fontSize: "var(--ofself-text-base)",
            }}
          >
            Loading…
          </span>
        </div>
      </div>
    );
  }

  if (filtered.length === 0) {
    return (
      <div className="message-list-card">
        <div
          className="message-list"
          style={{ alignItems: "center", justifyContent: "center" }}
        >
          <span
            style={{
              color: "var(--text-faint)",
              fontSize: "var(--ofself-text-base)",
            }}
          >
            {items.length === 0
              ? "No agents or plugins available"
              : `No matches for "/${query}"`}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="message-list-card"
      style={{ display: "block", overflow: "auto" }}
    >
      <div style={{ padding: "8px 0" }}>
        {filtered.map((item) => (
          <button
            key={item.key}
            onClick={() => onSelect(item)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 16px",
              width: "100%",
              border: "none",
              background: "none",
              cursor: "pointer",
              textAlign: "left",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background =
                "var(--hover-bg, rgba(255,255,255,0.04))")
            }
            onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                flexShrink: 0,
                background: item.type === "persona" ? "#566748" : "#2d6a4f",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontWeight: "var(--ofself-weight-semibold)",
                fontSize: "var(--ofself-text-base)",
              }}
            >
              {(item.name || "?")[0].toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span
                  style={{
                    fontWeight: "var(--ofself-weight-semibold)",
                    fontSize: "var(--ofself-text-base)",
                    color: "var(--text-primary)",
                  }}
                >
                  /
                  {item.slug ||
                    item.sub_entity_key ||
                    item.name?.toLowerCase().replace(/\s+/g, "-")}
                </span>
                <span
                  style={{
                    fontSize: "var(--ofself-text-sm)",
                    padding: "1px 5px",
                    borderRadius: 3,
                    background: item.type === "persona" ? "#566748" : "#2d6a4f",
                    color: "#fff",
                  }}
                >
                  {item.type === "persona" ? "AGENT" : "PLUGIN"}
                </span>
              </div>
              {item.description && (
                <div
                  style={{
                    fontSize: "var(--ofself-text-base)",
                    color: "var(--text-secondary)",
                    marginTop: 1,
                  }}
                >
                  {item.description.slice(0, 70)}
                </div>
              )}
            </div>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--text-faint)"
              strokeWidth="2"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        ))}
      </div>
    </div>
  );
}

function ComposeDirectMessage({
  createDM,
  createPluginDM,
  cancelComposeDM,
  currentUser,
  members,
  dmChannels,
  selectChannel,
  sendMessage,
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [showSlashPicker, setShowSlashPicker] = useState(false);
  const [slashQuery, setSlashQuery] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Deduplicate members
  const dedupedMembers = useMemo(() => {
    const seenId = new Set();
    const seenName = new Set();
    return (members || [])
      .filter((m) => {
        const uid = m.user_id || m.id;
        const name = (m.username || m.name || "").toLowerCase();
        if (
          !uid ||
          uid === currentUser?.id ||
          seenId.has(uid) ||
          seenName.has(name)
        )
          return false;
        seenId.add(uid);
        seenName.add(name);
        return true;
      })
      .map((m) => ({
        user_id: m.user_id || m.id,
        username: m.username || m.name || "User",
      }));
  }, [members, currentUser?.id]);

  function localSearch(q) {
    const qLower = q.toLowerCase();
    const selectedIds = new Set(selected.map((u) => u.user_id));
    return dedupedMembers.filter(
      (m) =>
        !selectedIds.has(m.user_id) &&
        m.username.toLowerCase().startsWith(qLower),
    );
  }

  function dedup(list) {
    const seenId = new Set();
    const seenName = new Set();
    return list.filter((u) => {
      const name = (u.username || "").toLowerCase();
      if (seenId.has(u.user_id) || seenName.has(name)) return false;
      seenId.add(u.user_id);
      seenName.add(name);
      return true;
    });
  }

  async function handleSearch(q) {
    setQuery(q);
    // "/" prefix → slash picker for agents/plugins
    if (q.startsWith("/") && !/\s/.test(q)) {
      setShowSlashPicker(true);
      setSlashQuery(q.slice(1).toLowerCase());
      setResults([]);
      return;
    }
    setShowSlashPicker(false);
    if (q.trim().length < 1) {
      setResults([]);
      return;
    }
    try {
      const res = await searchUsers(q.trim());
      const selectedIds = new Set(selected.map((u) => u.user_id));
      const qLower = q.trim().toLowerCase();
      const apiResults = (res.users || []).filter(
        (u) =>
          u.user_id !== currentUser?.id &&
          !selectedIds.has(u.user_id) &&
          (u.username || "").toLowerCase().startsWith(qLower),
      );
      setResults(
        dedup(apiResults.length > 0 ? apiResults : localSearch(q.trim())),
      );
    } catch {
      setResults(dedup(localSearch(q.trim())));
    }
  }

  // Find existing DM for selected users
  function findExistingDM(users) {
    if (users.length === 0) return null;
    if (users.length === 1) {
      const targetId = users[0].user_id;
      const targetName = (users[0].username || "").toLowerCase();
      return (
        (dmChannels || []).find((dm) => {
          if (dm.is_group_dm) return false;
          // Match by user ID
          if (dm.other_user_id === targetId) return true;
          // Match by username (fallback for ID mismatches between SDK and local)
          if (
            targetName &&
            (dm.other_username || "").toLowerCase() === targetName
          )
            return true;
          return false;
        }) || null
      );
    }
    return null;
  }

  const resolvedChannel = useMemo(
    () => findExistingDM(selected),
    [selected, dmChannels],
  );

  // When we resolve to an existing channel, silently select it so messages load
  useEffect(() => {
    if (resolvedChannel) {
      selectChannel(resolvedChannel, { keepCompose: true });
    }
  }, [resolvedChannel?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function addUser(user) {
    setSelected((prev) => [...prev, user]);
    setQuery("");
    setResults([]);
    inputRef.current?.focus();
  }

  function removeUser(userId) {
    setSelected((prev) => prev.filter((u) => u.user_id !== userId));
  }

  async function handleSend(text, fileId) {
    if (selected.length === 0) return;
    if (resolvedChannel) {
      // Already viewing the DM -- just cancel compose mode, message goes to active channel
      cancelComposeDM();
      return;
    }
    const userIds = selected.map((u) => u.user_id);
    const usernames = {};
    selected.forEach((u) => {
      if (u.username) usernames[u.user_id] = u.username;
    });
    try {
      const ch = await createDM(userIds.length === 1 ? userIds[0] : userIds, usernames);
      // Send the first message to the channel createDM just returned. Do NOT rely
      // on activeChannel here: this closure captured the previously-open channel,
      // so sending against it delivered the message to the wrong conversation while
      // an empty DM was created for the intended recipient. Passing `ch` routes it
      // explicitly to the new DM regardless of when the channel-switch state commits.
      if (text && ch?.id) {
        sendMessage(text, fileId || undefined, ch);
      }
    } catch (err) {
      console.error("Failed to create DM:", err);
    }
  }

  async function handleSlashSelect(item) {
    setShowSlashPicker(false);
    setQuery("");
    try {
      await createPluginDM(item.plugin_id, item.name, item.sub_entity_key);
    } catch (err) {
      console.error("Failed to create DM:", err);
    }
  }

  const showSuggestions =
    !showSlashPicker && query.trim().length >= 1 && results.length > 0;

  return (
    <>
      <header className="channel-header block__title">
        <div className="channel-header-inner block__title-surface">
          <div className="channel-header-left">
            <span className="channel-header-icon">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
            </span>
            <h2 className="channel-header-name">New Message</h2>
          </div>
        </div>
      </header>

      <div className="compose-dm-to-wrap">
        <div className="compose-dm-to">
          <span className="compose-dm-label">To:</span>
          {selected.map((u) => (
            <span key={u.user_id} className="dm-chip">
              {u.username}
              <button
                className="dm-chip-remove"
                onClick={() => removeUser(u.user_id)}
              >
                &times;
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            className="compose-dm-input"
            type="text"
            placeholder={
              selected.length > 0
                ? "Add more..."
                : "Search users or type / for agents & plugins"
            }
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
            onKeyDown={(e) => {
              if (showSlashPicker) return;
              if (e.key === "Enter" && results.length > 0) {
                e.preventDefault();
                addUser(results[0]);
              } else if (
                e.key === "Backspace" &&
                query === "" &&
                selected.length > 0
              ) {
                removeUser(selected[selected.length - 1].user_id);
              }
            }}
          />
        </div>
        {showSuggestions && (
          <ul className="ofself-menu compose-dm-dropdown">
            {results.map((user, i) => (
              <li key={user.user_id}>
                <button
                  className={`dm-search-result-item${i === 0 ? " highlighted" : ""}`}
                  onClick={() => addUser(user)}
                >
                  <div
                    className="dm-avatar"
                    style={{ background: hashColor(user.username) }}
                  >
                    {(user.username || "?")[0].toUpperCase()}
                  </div>
                  <span>{user.username}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showSlashPicker ? (
        <SlashDMPicker query={slashQuery} onSelect={handleSlashSelect} />
      ) : resolvedChannel ? (
        <>
          {/* MessageList's root IS .block__body — directly in the panel, no
              .message-list-card wrapper (dead thread-sleeve container). */}
          <MessageList />
          <div className="chat-bottom-area block__foot block__foot--flow">
            <MessageInput
              placeholder={`Message ${selected.map((u) => u.username).join(", ")}`}
            />
          </div>
        </>
      ) : (
        <>
          <div className="message-list-card">
            <div className="message-list">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "var(--text-faint)",
                  fontSize: "var(--ofself-text-base)",
                }}
              >
                {selected.length > 0
                  ? "Write a message to start the conversation"
                  : "Search for a person, or type / to start a chat with an agent or plugin"}
              </div>
            </div>
          </div>
          <div className="chat-bottom-area block__foot block__foot--flow">
            <MessageInput
              onSend={handleSend}
              placeholder={
                selected.length > 0
                  ? `Message ${selected.map((u) => u.username).join(", ")}`
                  : "Select someone to message..."
              }
            />
          </div>
        </>
      )}
    </>
  );
}

function ComposeChannel({
  groupId,
  groups,
  createGroupChannel,
  cancelComposeChannel,
  currentUser,
  groupChannelsByGroup,
}) {
  const [channelName, setChannelName] = useState("");
  const [description, setDescription] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  // Who can be invited: the GROUP's members, the same set the backend admits.
  // (The app-wide "people who share a channel with me" list is a different
  // set, and anyone from it outside the group would be refused.)
  const [members, setMembers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const nameRef = useRef(null);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  useEffect(() => {
    let cancelled = false;
    listGroupMembers(groupId)
      .then((res) => { if (!cancelled) setMembers(res.members || []); })
      .catch(() => { if (!cancelled) setMembers([]); });
    return () => { cancelled = true; };
  }, [groupId]);

  const group = (groups || []).find((g) => g.id === groupId);
  const existingNames = useMemo(() => {
    return new Set(
      (groupChannelsByGroup?.[groupId] || []).map((ch) =>
        ch.name?.toLowerCase(),
      ),
    );
  }, [groupChannelsByGroup, groupId]);
  const normalizedName = channelName.trim().toLowerCase().replace(/\s+/g, "-");
  const nameExists =
    normalizedName.length > 0 && existingNames.has(normalizedName);

  const dedupedMembers = useMemo(() => {
    const seenId = new Set();
    const seenName = new Set();
    return (members || [])
      .filter((m) => {
        const uid = m.user_id || m.user?.id || m.id;
        const name = (m.username || m.user?.username || m.name || "").toLowerCase();
        if (
          !uid ||
          uid === currentUser?.id ||
          seenId.has(uid) ||
          seenName.has(name)
        )
          return false;
        seenId.add(uid);
        seenName.add(name);
        return true;
      })
      .map((m) => ({
        user_id: m.user_id || m.user?.id || m.id,
        username: m.username || m.user?.username || m.name || "User",
      }));
  }, [members, currentUser?.id]);

  function handleMemberSearch(q) {
    setQuery(q);
    const selectedIds = new Set(selectedMembers.map((u) => u.user_id));
    const qLower = q.trim().toLowerCase();
    setResults(
      dedupedMembers.filter(
        (m) =>
          !selectedIds.has(m.user_id) &&
          (!qLower || m.username.toLowerCase().startsWith(qLower)),
      ),
    );
  }

  function addMember(user) {
    setSelectedMembers((prev) => [...prev, user]);
    setQuery("");
    setResults([]);
  }

  function removeMember(userId) {
    setSelectedMembers((prev) => prev.filter((u) => u.user_id !== userId));
  }

  async function handleCreate() {
    const name = channelName.trim().toLowerCase().replace(/\s+/g, "-");
    if (!name) return;
    setCreating(true);
    setError("");
    try {
      await createGroupChannel(
        groupId,
        name,
        description.trim(),
        selectedMembers.map((u) => u.user_id),
      );
      cancelComposeChannel();
    } catch (err) {
      const msg = err?.message || "Failed to create channel";
      setError(
        msg.toLowerCase().includes("already exists")
          ? `#${name} already exists`
          : msg,
      );
      setCreating(false);
    }
  }

  const showMemberSuggestions = results.length > 0;

  return (
    <>
      <div className="compose-dm-to-wrap">
        <div className="compose-dm-to">
          <span className="compose-dm-label">
            New Channel in {group?.name || "Group"}:
          </span>
          <input
            ref={nameRef}
            className="compose-dm-input"
            type="text"
            placeholder="channel-name"
            value={channelName}
            onChange={(e) => {
              setChannelName(e.target.value);
              setError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.preventDefault();
            }}
          />
          {nameExists && (
            <span
              style={{
                color: "var(--color-rust)",
                fontSize: "var(--ofself-text-base)",
                flexShrink: 0,
              }}
            >
              already exists
            </span>
          )}
        </div>
        <div className="compose-dm-to">
          <span className="compose-dm-label">Members:</span>
          {selectedMembers.map((u) => (
            <span key={u.user_id} className="dm-chip">
              {u.username}
              <button
                className="dm-chip-remove"
                onClick={() => removeMember(u.user_id)}
              >
                &times;
              </button>
            </span>
          ))}
          <input
            className="compose-dm-input"
            type="text"
            placeholder="Search users..."
            value={query}
            onChange={(e) => handleMemberSearch(e.target.value)}
            onFocus={() => {
              if (results.length === 0 && !query) handleMemberSearch("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && results.length > 0) {
                e.preventDefault();
                addMember(results[0]);
              } else if (
                e.key === "Enter" &&
                results.length === 0 &&
                query === "" &&
                channelName.trim()
              ) {
                e.preventDefault();
                handleCreate();
              } else if (
                e.key === "Backspace" &&
                query === "" &&
                selectedMembers.length > 0
              )
                removeMember(
                  selectedMembers[selectedMembers.length - 1].user_id,
                );
            }}
          />
        </div>
        {showMemberSuggestions && (
          <ul className="ofself-menu compose-dm-dropdown">
            {results.map((user, i) => (
              <li key={user.user_id}>
                <button
                  className={`dm-search-result-item${i === 0 ? " highlighted" : ""}`}
                  onClick={() => addMember(user)}
                >
                  <div
                    className="dm-avatar"
                    style={{ background: hashColor(user.username) }}
                  >
                    {(user.username || "?")[0].toUpperCase()}
                  </div>
                  <span>{user.username}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="message-list-card">
        <div className="message-list">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 12,
            }}
          >
            {error && (
              <div
                style={{
                  color: "var(--color-rust)",
                  fontSize: "var(--ofself-text-base)",
                }}
              >
                {error}
              </div>
            )}
            <button
              className="modal-btn modal-btn-primary"
              onClick={handleCreate}
              disabled={creating || !channelName.trim() || nameExists}
              style={{
                padding: "10px 28px",
                fontSize: "var(--ofself-text-base)",
              }}
            >
              {creating
                ? "Creating..."
                : `Create #${channelName.trim().toLowerCase().replace(/\s+/g, "-") || "channel"}`}
            </button>
          </div>
        </div>
      </div>
      <div className="chat-bottom-area block__foot block__foot--flow">
        <MessageInput
          placeholder={`Message #${channelName.trim().toLowerCase().replace(/\s+/g, "-") || "new-channel"}...`}
        />
      </div>
    </>
  );
}

export default function App() {
  if (window.location.pathname === "/callback") {
    return <CallbackHandler />;
  }

  return <ChatApp />;
}

function MarkdownPreview({ url }) {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    fetch(url)
      .then((r) => r.text())
      .then((text) => {
        marked.setOptions({ breaks: true, gfm: true });
        setHtml(marked.parse(text));
      })
      .catch(() => setHtml("<p>Failed to load file</p>"))
      .finally(() => setLoading(false));
  }, [url]);
  if (loading)
    return (
      <div className="file-preview-generic">
        <span className="file-preview-meta">Loading...</span>
      </div>
    );
  return (
    <div
      className="markdown-preview"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function FileForwardModal({
  file,
  channels,
  dmChannels,
  groups,
  groupChannelsByGroup,
  onForward,
  onClose,
}) {
  const [search, setSearch] = useState("");
  const q = search.toLowerCase().trim();

  // Build grouped sections
  const sections = [];

  // Group channels organized by group
  (groups || []).forEach((group) => {
    const groupChannels = (groupChannelsByGroup[group.id] || []).filter(
      (ch) => !q || ch.name.toLowerCase().includes(q),
    );
    if (groupChannels.length > 0) {
      sections.push({
        label: group.name,
        items: groupChannels.map((ch) => ({
          id: ch.id,
          name: ch.name,
          label: `# ${ch.name}`,
          type: "channel",
        })),
      });
    }
  });

  // DMs section
  const filteredDMs = (dmChannels || []).filter(
    (dm) =>
      !q || (dm.other_username || dm.name || "").toLowerCase().includes(q),
  );
  if (filteredDMs.length > 0) {
    sections.push({
      label: "Direct Messages",
      items: filteredDMs.map((dm) => ({
        id: dm.id,
        name: dm.other_username || dm.name,
        label: dm.other_username || dm.name,
        type: "dm",
      })),
    });
  }

  const totalItems = sections.reduce((n, s) => n + s.items.length, 0);

  return (
    <Modal onClose={onClose} title="Forward file" size="md" className="file-forward-modal">
          <div className="file-forward-inner">
            <div className="file-forward-search-wrap">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ color: "var(--text-faint)", flexShrink: 0 }}
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                className="file-forward-search"
                placeholder="Search channels or DMs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </div>
            <div className="file-forward-list">
              {sections.map((section) => (
                <div key={section.label}>
                  <div className="file-forward-section-label">
                    {section.label}
                  </div>
                  {section.items.map((target) => (
                    <button
                      key={target.id}
                      className="file-forward-item"
                      onClick={() => onForward(target.id)}
                    >
                      {target.type === "channel" ? (
                        <span
                          className="file-forward-icon"
                          style={{ color: "var(--text-muted)" }}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M4 9h16M4 15h16M10 3l-2 18M16 3l-2 18" />
                          </svg>
                        </span>
                      ) : (
                        <span
                          className="file-forward-avatar"
                          style={{ background: hashColor(target.name) }}
                        >
                          {(target.name || "?")[0].toUpperCase()}
                        </span>
                      )}
                      <span>{target.label}</span>
                    </button>
                  ))}
                </div>
              ))}
              {totalItems === 0 && (
                <div className="file-forward-empty">No matches found</div>
              )}
            </div>
          </div>
    </Modal>
  );
}

// THE EMBER MOTIF (uprooted from missions' sidebar, owner call 2026-08-16):
// scaffolding at 45% riding currentColor — dims and lifts with the cell —
// while the dots burn at FULL SHELL INK always. One drawing voice: 24-grid,
// 1.6 stroke, round caps.
// Pages whose own surface fills the main pane in place of the conversation.
const PAGES_OWNING_MAIN = new Set(["overview", "directory", "files"]);

/* THE PAGES, ONCE: the rail's doors (the Mac's RailDoor list, Root.swift),
   with the atrium above them as home. Messages is the page id "home" (its
   history in the address and the stored page); the atrium is "overview". */
// THE SHELL'S RAIL (owner call 2026-10-29, as Ink and Caliber seat it): the mark, then the ecosystem's seats at the
// foot, the launcher first; the avatar the kit's, last. Synapse's own seats are the app's rail (navItems) under the mark.
const RAIL_ITEMS = ["logo", "launcher", "personas", "search", "help", "settings"];
const SY_PAGES = [
  { id: "home", label: "Messages", object: <MessagesObject /> },
  { id: "activity", label: "Activity", object: <BellObject /> },
  { id: "huddles", label: "Huddles", object: <CallObject /> },
];
// a door's count, capped as the Mac's (and the Dock's) is
const countBadge = (n) => (n > 0 ? (n > 99 ? "99+" : n) : undefined);

function ChatApp() {
  const {
    authenticated,
    activeChannel,
    showThread,
    threadMessage,
    closeThread,
    selectChannel,
    hasUnreadDMs,
    hasNewActivity,
    newActivityCount,
    markActivitySeen,
    activeHuddle,
    startHuddle,
    leaveHuddle,
    currentUser,
    activeChannelMembers,
    createDM,
    createPluginDM,
    dmChannels,
    channels,
    composingDM,
    cancelComposeDM,
    composingChannel,
    cancelComposeChannel,
    createGroupChannel,
    groups,
    groupChannelsByGroup,
    members,
    groupMembers,
    addChannelMember,
    forwardMessage,
    startComposeDM,
    startComposeChannel,
    toggleChannelMute,
    mutedChannels,
    toggleChannelPin,
    pinnedChannels,
    markAsUnread,
    deleteChannel,
    leaveChannel,
    bulkEditMode,
    setBulkEditMode,
    bulkSelectedMessages,
    setBulkSelectedMessages,
    activeGroup,
    selectGroup,
    unreadCounts,
    sendMessage,
    messages,
    activeMessages,
    recordingsHuddleId,
    closeRecordingsSidebar,
    epStatus,
    userAvatars,
    uploadAvatar,
    syncUrl,
    activeWorld,
  } = useChat();
  const callIslandState = useCallIslandState(activeChannel);
  // What the shell draws for each space, photos included (utils/shellSpaces).
  const spaces = useMemo(() => shellSpaces(groups, activeGroup), [groups, activeGroup]);

  // The channel's freshest stamp, from whichever store holds it: the loaded
  // thread's tail or the raw feed's. (`timestamp` is the feed's field;
  // created_at is kept for older payload shapes.) Derived ONCE — both ledgers
  // (the header's and the About tab's) are the same fact.
  const lastMessageStamp = useMemo(() => {
    const tail = (activeMessages?.length ? activeMessages[activeMessages.length - 1] : null)
      || (messages?.length ? messages[messages.length - 1] : null);
    return tail?.timestamp || tail?.created_at || null;
  }, [activeMessages, messages]);

  const [showMembers, setShowMembers] = useState(false);
  const [channelDetailTab, setChannelDetailTab] = useState("members");
  const [threadClosing, setThreadClosing] = useState(false);
  // On refresh with a persisted thread, suppress the open animation.
  // The sleeve gets animation:none until the user explicitly closes and reopens.
  const [threadSkipAnim, setThreadSkipAnim] = useState(() => {
    try {
      return !!JSON.parse(localStorage.getItem("paradigm_cache_threadMessage"));
    } catch {
      return false;
    }
  });

  // Just flip the flag — Radix Dialog keeps the panel mounted through its
  // [data-state="closed"] exit animation (see .ofself-dialog in Modal.css), so
  // there's no manual closing-state/timeout to race with Radix's own lifecycle.
  function closeMembers() {
    setShowMembers(false);
  }
  const prevShowThread = useRef(showThread);
  const topBarRef = useRef(null);
  const sidebarResizableRef = useRef(null);
  const chatMainRef = useRef(null); // conversation; the thread seam grip anchors to its right edge
  const [activePage, setActivePage] = useState(() => {
    /* THE ADDRESS WINS WHERE IT SAYS SOMETHING (2026-08-21). A path naming a
       conversation — /:group/:channel or /dm/:user — is a deep link, and the
       page it belongs to is Messages; opening it on the home page would make
       a shared link land somewhere other than where it points. A SCOPE ROOT
       (/ or /:group) names no conversation, so the session decides.

       A FRESH SESSION OPENS ON THE HOME PAGE: the app used to open on
       whichever section happened to be first, which meant it had no front
       door — you arrived mid-conversation with no account of what was
       waiting. Within a session the page you left is the page you return to.
       ("huddles" joins the valid list here: it was reachable from the column
       but not restorable, so a reload silently dropped you back to
       Messages.) */
    const seg = window.location.pathname.split("/").filter(Boolean);
    const namesAConversation = seg[0] === "dm" ? !!seg[1] : seg.length > 1;
    if (namesAConversation) return "home";
    const saved = sessionStorage.getItem("paradigm_active_page") || "overview";
    const validPages = ["overview", "home", "dms", "activity", "huddles"];
    return validPages.includes(saved) ? saved : "overview";
  });
  const [selectedFile, setSelectedFile] = useState(null);
  // PAGE-SWAP FLAG — true for the navigation frame only; suppresses the
  // panel width-glide so the sidebar swaps WITH its page (handlePageChange).
  const pageSwapTimer = useRef(null);
  // FOCUS MODE — the conversation runs edge-to-edge (Ink's grammar): the
  // shell rail folds, sidebar + agent close and RESTORE on exit; the tool
  // rail goes phantom with only the exit cell visible.
  const [focusMode, setFocusMode] = useState(false);
  const [selectedDirMember, setSelectedDirMember] = useState(null);
  const [forwardingFile, setForwardingFile] = useState(null);

  // Theme tracking (for conditional GeometricBackground)
  const [currentTheme, setCurrentTheme] = useState(() => {
    const saved = localStorage.getItem("paradigm_theme") || "moss-light";
    return saved === "moss" || saved === "moss-light" ? saved : "moss-light";
  });
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setCurrentTheme(
        document.documentElement.getAttribute("data-theme") || "light",
      );
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);

  // Grand total across every channel and DM — the section column's
  // MESSAGES counter (refractions' .section-count grammar).
  const totalUnread = useMemo(
    () => Object.values(unreadCounts).reduce((sum, n) => sum + (n || 0), 0),
    [unreadCounts],
  );

  // Per-space unread count tracking
  const spaceUnreads = useMemo(() => {
    const result = { home: 0 };
    // Home = DMs
    dmChannels.forEach((dm) => {
      if (unreadCounts[dm.id]) result.home += unreadCounts[dm.id];
    });
    // Each group
    (groups || []).forEach((g) => {
      result[g.id] = 0;
      (groupChannelsByGroup[g.id] || []).forEach((ch) => {
        if (unreadCounts[ch.id]) result[g.id] += unreadCounts[ch.id];
      });
    });
    return result;
  }, [dmChannels, groups, groupChannelsByGroup, unreadCounts]);

  // Are there unreads in OTHER spaces (not the current one)?
  const hasOtherSpaceUnreads = useMemo(() => {
    if (!activeGroup) {
      return (groups || []).some((g) => spaceUnreads[g.id] > 0);
    }
    if (spaceUnreads.home > 0) return true;
    return (groups || []).some(
      (g) => g.id !== activeGroup.id && spaceUnreads[g.id] > 0,
    );
  }, [activeGroup, groups, spaceUnreads]);

  // Space switcher
  const [showSpaceSwitcher, setShowSpaceSwitcher] = useState(false);
  const [spaceSwitcherLeaving, setSpaceSwitcherLeaving] = useState(false);
  const spaceSwitcherRef = useRef(null);

  function closeSpaceSwitcher() {
    if (!showSpaceSwitcher || spaceSwitcherLeaving) return;
    setSpaceSwitcherLeaving(true);
    setTimeout(() => {
      setShowSpaceSwitcher(false);
      setSpaceSwitcherLeaving(false);
    }, 180);
  }

  useEffect(() => {
    if (!showSpaceSwitcher) return;
    const close = (e) => {
      if (
        spaceSwitcherRef.current &&
        !spaceSwitcherRef.current.contains(e.target)
      )
        closeSpaceSwitcher();
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [showSpaceSwitcher, spaceSwitcherLeaving]);

  function switchToSpace(group) {
    if (group) {
      selectGroup(group);
    } else {
      // "Home" — clear activeGroup, select first DM if available
      selectGroup(null);
    }
    closeSpaceSwitcher();
    if (activePage !== "home") handlePageChange("home");
  }

  // Connect an unauthorized group: send the user through the Paradigm
  // authorize flow scoped to that group (client_id + group_id). On return to
  // /callback the app reloads and ep-status reports the group as authorized,
  // flipping its "Connect" row to a normal switchable space.
  function connectToGroup(group) {
    if (!group?.id) return;
    const sdkFrontend =
      import.meta.env.VITE_SDK_FRONTEND_URL || "http://localhost:3000";
    const redirectUri = `${window.location.origin}/callback`;
    window.location.href =
      `${sdkFrontend}/authorize?client_id=${CLIENT_ID}` +
      `&group_id=${encodeURIComponent(group.id)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}&scope=read`;
  }

  // Navigation history
  const navHistoryRef = useRef([
    { page: "home", channelId: null, channel: null },
  ]);
  const navIndexRef = useRef(0);
  const [navState, setNavState] = useState({
    canGoBack: false,
    canGoForward: false,
  });
  const isNavigatingRef = useRef(false);
  // The list's default starting width: ~2 cells of the board grid (min(1240, 100vw−320) ÷ 6).
  // Used only when no persisted width exists (first launch or cleared storage).
  const sidebarInitial = useMemo(() => {
    if (typeof window === "undefined") return 350;
    const gap = 9;
    const cluster = Math.min(1240, window.innerWidth - 320);
    const fitCell = (cluster - 5 * gap) / 6;
    return Math.round(2 * fitCell + gap); // 2 cells = 2·cell + 1 gap
  }, []);
  // NATURAL grid alignment (no manual calibration): anchor the field lattice to the cluster's ACTUAL
  // measured top-left, so the cells line up by construction (a pure calc drifts sub-pixel against the real
  // flex-centred box). −gap/2 centres the lines in the gutters. Re-measures on resize / any layout change;
  // setting the var never reflows the cluster, so there's no loop.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    const sync = () => {
      const el = document.querySelector(".content-card");
      if (!el) return;
      const r = el.getBoundingClientRect();
      const gapStr = getComputedStyle(root)
        .getPropertyValue("--cell-gap")
        .trim();
      const gap = gapStr ? parseFloat(gapStr) || 0 : 9;
      root.style.setProperty(
        "--tile-grid-x",
        `${(r.left - gap / 2).toFixed(2)}px`,
      );
      root.style.setProperty(
        "--tile-grid-y",
        `${(r.top - gap / 2).toFixed(2)}px`,
      );
    };
    const raf = requestAnimationFrame(sync);
    const el = document.querySelector(".content-card");
    let ro;
    if (el && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(sync);
      ro.observe(el);
    }
    window.addEventListener("resize", sync);
    return () => {
      cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      window.removeEventListener("resize", sync);
    };
  }, []);
  // THE LIST'S SEAM (the Mac's .resizableColumn("synapse.listWidth", 330,
  // 240...560)): the conversation list takes its width from the hand, kept
  // per device. The list has no thin state: the rail's sidebar seat (⌘\)
  // hides it whole and brings it back at the width it was left at.
  const {
    width: sidebarWidth,
    isDragging,
    separatorProps: sidebarResizeProps,
  } = useResizablePanel({
    edge: "right",
    initial: sidebarInitial,
    min: SIDEBAR_MIN,
    max: SIDEBAR_MAX,
    storageKey: "synapse_sidebar_tiled-width",
    cssVar: "--shell-sidebar-w",
  });


  // THE LIST IS SHOWN OR HIDDEN (the Mac's `ui.column`): the rail's sidebar
  // seat and ⌘\ flip it; going to a page with a list brings it back.
  const [sidebarOpen, toggleSidebar, setSidebarOpen] = usePanelOpen("synapse-sidebar-open", true);
  const historySteps = useHistorySteps();

  function containerWidthForResize() {
    const card = sidebarResizableRef.current?.parentElement; // .content-card
    return card?.getBoundingClientRect().width || window.innerWidth;
  }

  // THE RULE: every seam between two adjacent panels is draggable from BOTH sides;
  // only the outermost edges aren't. The sidebar seam is owned at App level
  // (usePanel) so BOTH the sidebar's right edge and the chat's left edge can
  // drive it. The thread seam is lifted here for the SAME reason — so the chat's
  // right edge can drive it too (it lived inside ThreadDock, out of the chat's reach).
  const {
    width: threadWidth,
    setWidth: setThreadWidth,
    isDragging: threadDragging,
    separatorProps: threadResizeProps,
  } = useResizablePanel({
    edge: "left",
    initial: sidebarInitial,
    min: THREAD_MIN,
    // Read the sidebar's ACTUAL rendered width from the DOM at drag-start (the
    // bounds are snapshotted once at press time by useResizablePanel). React state
    // (`sidebarWidth`) is stale in focus mode — CSS forces the sidebar to 0px but
    // the state stays at the last expanded value, artificially capping the thread.
    // DOM measurement is always current: 0 in focus mode, real px otherwise.
    max: () => {
      const actualSidebarW =
        sidebarResizableRef.current?.getBoundingClientRect().width ??
        sidebarWidth;
      return Math.max(
        THREAD_MIN,
        containerWidthForResize() - actualSidebarW - CHAT_MIN,
      );
    },
    storageKey: "synapse_thread_tiled",
    cssVar: "--thread-w",
  });

  // THE CHAT APP — sidebar + conversation + thread are ONE panel with internal draggable dividers.
  // Activity is NOT its own tile — it's a tab inside the sidebar (the Messages/Activity capsule).

  // Record navigation history on page/channel changes
  const updateNavButtons = useCallback(() => {
    setNavState({
      canGoBack: navIndexRef.current > 0,
      canGoForward: navIndexRef.current < navHistoryRef.current.length - 1,
    });
  }, []);

  useEffect(() => {
    if (isNavigatingRef.current) {
      isNavigatingRef.current = false;
      return;
    }
    const entry = {
      page: activePage,
      channelId: activeChannel?.id || null,
      channel: activeChannel || null,
    };
    const history = navHistoryRef.current;
    const idx = navIndexRef.current;
    const current = history[idx];
    if (
      current &&
      current.page === entry.page &&
      current.channelId === entry.channelId
    )
      return;
    const trimmed = history.slice(0, idx + 1);
    trimmed.push(entry);
    if (trimmed.length > 50) trimmed.shift();
    navHistoryRef.current = trimmed;
    navIndexRef.current = trimmed.length - 1;
    updateNavButtons();
  }, [activePage, activeChannel?.id, updateNavButtons]);

  const goBack = useCallback(() => {
    if (navIndexRef.current <= 0) return;
    navIndexRef.current -= 1;
    const target = navHistoryRef.current[navIndexRef.current];
    isNavigatingRef.current = true;
    setActivePage(target.page);
    if (target.channel) mobileSelectChannel(target.channel);
    setShowMembers(false);
    updateNavButtons();
  }, [selectChannel, updateNavButtons]);

  const goForward = useCallback(() => {
    if (navIndexRef.current >= navHistoryRef.current.length - 1) return;
    navIndexRef.current += 1;
    const target = navHistoryRef.current[navIndexRef.current];
    isNavigatingRef.current = true;
    setActivePage(target.page);
    if (target.channel) mobileSelectChannel(target.channel);
    setShowMembers(false);
    updateNavButtons();
  }, [selectChannel, updateNavButtons]);

  // THE CONVERSATION OUTLIVES THE PAGE: switching pages swaps the list, never
  // the open conversation. Only a page that owns the main pane itself hides it.
  const showMainChat = activeChannel && !PAGES_OWNING_MAIN.has(activePage);
  // THE COMPOSER'S DOORS, opened from the end rail: Attach the file picker,
  // Voice (the kit's useDictation: the browser records, the server
  // transcribes) the words. The conversation's composer, never a thread's.
  const composerDoors = useRef(null);
  const voice = useDictation({
    transcribe: transcribeDictation,
    onText: (text) => composerDoors.current?.insert(text),
  });

  /* THE ADDRESS FOLLOWS THE PAGE. One writer, one rule: the home page is the
     scope's own root (/ or /:group — the app's front door, the address you
     would type), and a page showing a conversation is that conversation's
     path. ChatContext owns the grammar (syncUrl → pushUrl); this owns which
     of the two the app is currently showing. Pages with no address of their
     own (Activity, Huddles) leave the last one standing rather than lying
     about a conversation that is not on screen. */
  useEffect(() => {
    if (activePage === "overview") syncUrl(null);
    else if (showMainChat) syncUrl(activeChannel);
  }, [activePage, showMainChat, activeChannel?.id, activeGroup?.id, syncUrl]);

  // Module layout — tracks each pane's visibility (sidebar / messages / thread) so opening a
  // thread or collapsing the sidebar stays in sync across the app.
  const { layout, openModule, closeModule } = useModuleLayout();

  // Cmd+K to open search, Escape to close settings
  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        topBarRef.current?.open();
      }
      if (e.key === "Escape") {
        // Close modals in priority order: members/channel detail > thread
        if (showMembers) {
          closeMembers();
          return;
        }
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showMembers]);

  function mobileSelectChannel(ch, opts) {
    selectChannel(ch, opts);
    // a conversation chosen while the atrium holds the stage (the phone's list
    // page stands beside it) opens on Messages, or the press changed nothing
    if (ch && activePage === "overview") handlePageChange("home");
    // Auto-open messages module when selecting a channel
    if (ch && layout.messages?.visible === false) {
      openModule("messages");
    }
    // Advance to the conversation panel in mobile — even if the same channel is
    // re-tapped, the user's intent is to view it, not stay on the sidebar.
    if (ch && isMobilePanels) goToPanel(1);
  }

  // Settings live in the SHELL's window now (the rail cog / ShellSettings):
  // Synapse contributes its panes via Shell's `settings` prop and
  // opens the window programmatically here (keyboard path, deep links).
  function openSettings() {
    // the window's first Synapse pane (Profile, which used to be this seat,
    // retired 2026-08-23 — see the `settings` prop below)
    openShellSettings("synapse-appearance");
  }

  function handlePageChange(page) {
    if (page === "settings") {
      openSettings();
      return;
    }
    // THE SIDEBAR IS THE PAGE'S, not the app's (owner call 2026-08-21):
    // navigating between pages must SWAP the panels with the page, never
    // read as a global open/close — the kit's width-glide made "go home"
    // look like it was closing the sidebar. The swap flag suppresses the
    // panel transitions for the navigation frame (the shell's html[data-page-swap], SplitView.css); the glide
    // stays for the things that ARE panel toggles (the wing, the rail).
    if (page !== activePage) {
      const root = document.documentElement;
      root.setAttribute("data-page-swap", "");
      clearTimeout(pageSwapTimer.current);
      pageSwapTimer.current = setTimeout(() => root.removeAttribute("data-page-swap"), 150);
    }
    setActivePage(page);
    sessionStorage.setItem("paradigm_active_page", page);
    // a page with a list brings the list back (the Mac's ui.go)
    if (page !== "overview") setSidebarOpen(true);
    setShowMembers(false);
    if (page !== "files") setSelectedFile(null);
    if (page !== "directory") setSelectedDirMember(null);
    if (page === "activity") {
      markActivitySeen();
    }
    // Re-open sidebar module if it was closed
    if (layout.sidebar?.visible === false) {
      openModule("sidebar");
    }
  }

  function handleDMSelect(dm) {
    mobileSelectChannel(dm);
  }

  function handleGroupSelect(channel) {
    mobileSelectChannel(channel);
  }

  function handleChannelSearch() {
    topBarRef.current?.openWithFilter(activeChannel);
  }

  // THE CHANNEL TOOLS — one declaration for the conversation's actions,
  // rendered as header cells on every width (the ToolRail retired 2026-08-16).
  const inHuddleCall = activeHuddle?.participants?.some(
    (p) => p.user_id === currentUser?.id,
  );
  const channelMuted = activeChannel
    ? mutedChannels.has(activeChannel.id)
    : false;
  // Focus mode: the conversation alone, the rails slid away.
  useEffect(() => {
    // THE RETRACTION IS THE SHELL'S (setFocusMode, kit 0.166.0). Synapse's
    // own data-sy-focus block is retired: it zeroed --rail-stack-w on .shell
    // and stood the uprights down, but it never touched the GAP RAIL — the
    // .shell padding, its ::after strip and the fixed mark tile — so the
    // conversation ran edge-to-edge against an empty column with a hairline
    // down it (owner report 2026-08-23, "clicking full screen should actually
    // go full screen"). Ink hit the identical half-fix, which is why the law
    // went up to the kit. The kit also exempts [data-seat="fullscreen"] from
    // the corner fade, so the way OUT stays lit.
    // ALIASED ON IMPORT: this component already owns a `setFocusMode` state
    // setter, and an unaliased import would be shadowed by it inside the
    // component — the call would flip synapse's own state instead of stamping
    // the attribute, and the retraction would never fire.
    // The list stands down with it and comes back as it was: the list's
    // leadingOpen reads focusMode, and sidebarOpen is never touched (the
    // Mac's hasSide, !ui.focus && ui.column).
    applyShellFocus(focusMode);
  }, [focusMode]);

  /* THE CORNER IS THE CHANNEL'S — so its tools stand only while a channel is
     actually on screen (owner report 2026-08-21: the huddle and the bell were
     out on the home page). `activeChannel` is the wrong gate: a channel stays
     selected in the background while you read the ledger, and the seats were
     acting on a conversation nobody could see. showMainChat is the same
     question the conversation panel itself asks. */
  const channelTools = showMainChat
    ? [
        ...(!activeChannel.is_plugin_dm && !activeChannel.is_persona_dm
          ? [
              {
                key: "call",
                label: inHuddleCall
                  ? "Leave call"
                  : activeHuddle
                    ? "Join call"
                    : "Start call",
                className: activeHuddle ? "header-action-huddle-active" : "",
                onClick: inHuddleCall ? leaveHuddle : startHuddle,
                icon: <VideoIcon size={18} />,
              },
            ]
          : []),
        /* (the header's search cell retired 2026-08-16 — ONE search, the
           corner's; restore this block for the channel-scoped spotlight)
        { key: "search", label: "Search", onClick: () => handleChannelSearch(), icon: null }, */
      ]
    : [];

  // THE COMMAND CENTER — the shell's bottom dock (⌘J): verb-grammar palette +
  // the session's flight-recorder transcript. ONE list (src/commands.js)
  // behind it, rebuilt when the live conversation set changes; every channel
  // and DM rides in as a quick-switch item, the same injection Ink does with
  // notes. Deps are the actual inputs — the handlers are redeclared each
  // render, so depending on them would rebuild every render for nothing.
  const dockCommands = useMemo(
    () =>
      buildSynapseCommands({
        goTo: handlePageChange,
        composeDM: startComposeDM,
        composeChannel: activeGroup
          ? () => startComposeChannel(activeGroup.id)
          : null,
        toggleSidebar,
        openSettings,
        openSearch: () => topBarRef.current?.open(),
        // Full screen — only offered when there's a conversation to expand
        // (or one to exit from; without the guard, entering focus with no
        // channel open would strand the user: the rail's exit cell only
        // renders with the conversation).
        focusMode,
        toggleFocus:
          showMainChat || focusMode ? () => setFocusMode((v) => !v) : null,
        conversations: [
          ...(channels || []).map((ch) => ({
            id: ch.id,
            label: `# ${ch.name}`,
            open: () => selectChannel(ch),
          })),
          ...(dmChannels || []).map((ch) => ({
            id: ch.id,
            label: dmDisplayName(ch),
            open: () => selectChannel(ch),
          })),
        ],
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [channels, dmChannels, activeGroup, focusMode, showMainChat],
  );

  // A channel-search request bridges here via an event: open the rail's universal spotlight
  // pre-filtered to the clicked conversation.
  useEffect(() => {
    const onChannelSearch = (e) =>
      topBarRef.current?.openWithFilter(e.detail?.channel || activeChannel);
    window.addEventListener("paradigm:channel-search", onChannelSearch);
    return () =>
      window.removeEventListener("paradigm:channel-search", onChannelSearch);
  }, [activeChannel]);

  // Exit edit mode when channel changes
  useEffect(() => {
    setBulkEditMode(false);
    setBulkSelectedMessages(new Set());
  }, [activeChannel?.id]);

  function handleMembersToggle() {
    const next = !showMembers;
    // the Members seat opens the details on their Members tab, as the Mac's
    if (next) setChannelDetailTab("members");
    setShowMembers(next);
    if (next && showThread) closeThread();
  }

  // The thread counts as OPEN only when it actually has a message — `showThread`
  // can be stale-true (cache-initialised, not always reset on channel switch),
  // which would otherwise render a blank thread panel (and, on mobile, an extra
  // blank slide that "comes in from the right").
  const effectiveShowThread =
    showThread && !!threadMessage && activePage !== "overview";

  // ── Mobile single-panel slide nav (shell mechanism) ─────────────────────────
  // Below the breakpoint the .content-card row shows ONE panel at a time:
  // sidebar(0) → chat(1) → thread(2). Selecting a channel advances to chat;
  // opening a thread advances to it; the chevron back button (panel headers)
  // walks the index down.
  const mobilePanelCount = effectiveShowThread ? 3 : 2;
  // ONE breakpoint: a single STABLE width below which the sidebar + conversation
  // (and the thread, when open) can no longer sit side-by-side, so the shell drops
  // to the single-panel slide track. Derived from CONSTANTS, never the LIVE sidebar
  // width. A live width feeds back on itself (narrowing shrinks the sidebar, which
  // lowers the breakpoint, which never crosses it), the hysteresis that made
  // "going back to wide" require an over-stretch. Width only, no height clause.
  const mobileBp =
    RAIL_COLLAPSED_W +
    SIDEBAR_NOMINAL +
    CHAT_MIN +
    (effectiveShowThread ? THREAD_MIN : 0);
  // The DEEPEST open panel: the app's nav depth, handed to the shell hook so it
  // lands here on entering mobile and slides here when a deeper panel opens.
  const activePanelDepth = effectiveShowThread
    ? 2
    : /* the home page is a CONTENT destination, so mobile slides to the
         content panel for it exactly as it does for a channel */
      activeChannel || activePage === "overview"
      ? 1
      : 0;
  const {
    isMobile: isMobilePanels,
    index: mobilePanelIndex,
    goTo: goToPanel,
    goBack: goBackPanel,
    canGoBack: canGoBackPanel,
    panelsProps: mobilePanelsProps,
  } = useMobilePanels({
    count: mobilePanelCount,
    breakpoint: mobileBp,
    activeIndex: activePanelDepth,
  });
  // The single-panel SLIDE track on mobile. ONE boolean: also handed to
  // Shell (the `mobile` prop) so the rail-fold and this track are the
  // same signal and can never disagree (no white-space, no no-slide desync).
  const slideMobile = isMobilePanels;
  // the composer hands its doors to the end rail where one stands; on the phone it keeps its +
  const composerFoot = (
    <div className="chat-bottom-area block__foot block__foot--flow">
      <DictationStatus state={voice.state} error={voice.error} />
      <MessageInput doors={slideMobile ? null : composerDoors} />
    </div>
  );
  // THE THREAD HOLDS THE RIGHT EDGE TOO (owner report 2026-09-20: "when we
  // open a right sidebar thread, the top right buttons of the chat panel need
  // to slide to the left as it slides open too — it already does for the
  // agent personas"). The wall is a kit SlideOutPanel and claims its edge
  // itself; the thread is a flat .block that squeezes the chat, so it makes
  // the same claim through the kit's door (0.249.0) and the corner cluster
  // steps clear by the same house ease. Not on mobile — the thread is a
  // full-width slide there, and the corner has nothing to clear.
  useRightEdgeClaim(effectiveShowThread && !isMobilePanels ? threadWidth : 0);
  // (Entering-mobile landing and thread-open advance are owned by the hook via
  // activePanelDepth; re-tapping the active channel is handled imperatively in
  // mobileSelectChannel, since that's a user EVENT, not a depth change.)

  // Auto-open/close thread module when thread is shown/hidden
  useEffect(() => {
    if (effectiveShowThread && layout.thread?.visible === false) {
      openModule("thread");
    }
    if (
      !effectiveShowThread &&
      !threadClosing &&
      layout.thread?.visible === true
    ) {
      closeModule("thread");
    }
  }, [effectiveShowThread, threadClosing]);

  // Keep the thread MOUNTED through its close so the kit's width-ease (basis → 0)
  // can play out before unmount. useLayoutEffect sets threadClosing before paint so
  // there's no frame where threadVisible=false flickers the panel away. The timer
  // matches the kit's width transition (~0.4s) — unmount any sooner and it'd snap.
  // On cold mount, --thread-w may be set in localStorage from a previous session.
  // If the thread is not open, remove it immediately so the panel doesn't render
  // with a phantom non-zero width before React can correct it.
  useEffect(() => {
    if (!effectiveShowThread)
      document.documentElement.style.removeProperty("--thread-w");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useLayoutEffect(() => {
    const wasOpen = prevShowThread.current;
    prevShowThread.current = effectiveShowThread;
    if (wasOpen && !effectiveShowThread) {
      document.documentElement.style.removeProperty("--thread-w");
      setThreadClosing(true);
      setThreadSkipAnim(false);
      const timer = setTimeout(() => setThreadClosing(false), 420);
      return () => clearTimeout(timer);
    }
  }, [effectiveShowThread]);

  // THE PROPERTIES PLATE (owner call 2026-08-22): the channel's name +
  // ledger left the header band — they open from the corner's properties
  // seat as a float plate instead. Esc and press-outside are the shell
  // <PropertiesPlate>'s job since 2026-08-23; the one dismissal that is
  // synapse's own is "you changed channel, this ledger is stale".
  const [channelPropsOpen, setChannelPropsOpen] = useState(false);
  useEffect(() => { setChannelPropsOpen(false); }, [activeChannel?.id]);

  // Auth gate — placed AFTER every hook so the hook order is identical whether or
  // not the user is signed in. (Returning early ABOVE the hooks changes the hook
  // count when `authenticated` flips on login → "rendered more hooks" → a prod-only
  // crash reading the hook list's length.)
  if (!authenticated) return <LoginGate />;

  const threadVisible = effectiveShowThread || threadClosing;

  function renderSidePanel() {
    switch (activePage) {
      case "dms":
        return <DMsPage onSelectDM={handleDMSelect} />;
      case "groups":
        return <GroupsPage onSelectGroup={handleGroupSelect} />;
      case "directory":
        return (
          <DirectoriesPage
            selectedMember={selectedDirMember}
            onSelectMember={(m) => {
              setSelectedDirMember(m);
            }}
          />
        );
      case "activity":
        return <ActivityPage onNavigate={() => {}} />;
      case "files":
        return (
          <FilesPage
            selectedFile={selectedFile}
            onSelectFile={setSelectedFile}
          />
        );
      case "huddles":
        return <HuddlesPage />;
      case "home":
      default:
        return (
          <Sidebar
            onSelectChannel={mobileSelectChannel}
            onOpenAtrium={() => handlePageChange("overview")}
          />
        );
    }
  }

  // Render the main panel content (messages area)
  function renderMainContent() {
    if (composingDM) {
      return (
        <ComposeDirectMessage
          createDM={createDM}
          createPluginDM={createPluginDM}
          cancelComposeDM={cancelComposeDM}
          currentUser={currentUser}
          members={members}
          dmChannels={dmChannels}
          selectChannel={selectChannel}
          sendMessage={sendMessage}
        />
      );
    }
    if (composingChannel) {
      return (
        <ComposeChannel
          groupId={composingChannel}
          groups={groups}
          groupChannelsByGroup={groupChannelsByGroup}
          createGroupChannel={createGroupChannel}
          cancelComposeChannel={cancelComposeChannel}
          currentUser={currentUser}
        />
      );
    }
    if (showMainChat) {
      return (
        <>
          {/* MessageList's root IS the shell body (.block__body) — it sits
              DIRECTLY inside the panel, no wrapper. (The old .message-list-card
              was a flex-row container for the dead thread-sleeve; the thread is a
              flat sibling panel now, so the wrapper is gone.) */}
          <MessageList />
          {composerFoot}
        </>
      );
    }
    if (activePage === "directory") {
      return (
        <div className="conversation-inner">
          <DirectoryDetail
            member={selectedDirMember}
            onStartDM={() => handlePageChange("dms")}
            onNavigateChannel={() => handlePageChange("home")}
          />
        </div>
      );
    }
    if (activePage === "files" && selectedFile) {
      return (
        <div className="file-preview-panel">
          <div className="file-preview-layout">
            <div className="file-meta-sidebar">
              <h3 className="file-meta-filename">
                {selectedFile.original_filename}
              </h3>
              <div className="file-meta-section">
                <span className="file-meta-label">Shared by</span>
                <div className="file-meta-sender">
                  {(() => {
                    const name = selectedFile.uploader_name || "Unknown";
                    const colors = Array.from(
                      { length: 10 },
                      (_, i) => `var(--avatar-${i})`,
                    );
                    const color =
                      colors[
                        Math.abs(
                          name
                            .split("")
                            .reduce((a, c) => a + c.charCodeAt(0), 0),
                        ) % colors.length
                      ];
                    return (
                      <span
                        className="file-meta-avatar"
                        style={{ background: color }}
                      >
                        {name[0]?.toUpperCase()}
                      </span>
                    );
                  })()}
                  <span>{selectedFile.uploader_name || "Unknown"}</span>
                </div>
              </div>
              <div className="file-meta-section">
                <span className="file-meta-label">Date</span>
                <span className="file-meta-value">
                  {selectedFile.created_at
                    ? new Date(selectedFile.created_at).toLocaleDateString(
                        undefined,
                        { month: "short", day: "numeric", year: "numeric" },
                      )
                    : ""}
                  {selectedFile.created_at
                    ? ` at ${formatTime(selectedFile.created_at)}`
                    : ""}
                </span>
              </div>
              <div className="file-meta-section">
                <span className="file-meta-label">Shared in</span>
                <button
                  className="file-meta-channel-link"
                  onClick={() => {
                    const allCh = [...(channels || []), ...(dmChannels || [])];
                    const ch = allCh.find(
                      (c) => c.id === selectedFile.channel_id,
                    );
                    if (ch) {
                      selectChannel(ch);
                      handlePageChange("home");
                    }
                  }}
                >
                  {selectedFile.is_dm ? (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  ) : (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M4 9h16M4 15h16M10 3l-2 18M16 3l-2 18" />
                    </svg>
                  )}
                  <span>
                    {selectedFile.is_dm
                      ? selectedFile.channel_name || "Direct Message"
                      : selectedFile.channel_name || "Unknown channel"}
                  </span>
                </button>
                {selectedFile.thread_id && (
                  <span className="file-meta-thread-note">in a thread</span>
                )}
              </div>
              <div className="file-meta-section">
                <span className="file-meta-label">Type</span>
                <span className="file-meta-value">
                  {selectedFile.mimetype || "Unknown"}
                </span>
              </div>
              <div className="file-meta-section">
                <span className="file-meta-label">Size</span>
                <span className="file-meta-value">
                  {selectedFile.size < 1024
                    ? `${selectedFile.size} B`
                    : selectedFile.size < 1024 * 1024
                      ? `${(selectedFile.size / 1024).toFixed(1)} KB`
                      : `${(selectedFile.size / (1024 * 1024)).toFixed(1)} MB`}
                </span>
              </div>
              <div className="file-meta-actions">
                <button
                  className="file-meta-btn"
                  onClick={() => {
                    fetch(getFileUrl(selectedFile.id))
                      .then((r) => r.blob())
                      .then((blob) => {
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = selectedFile.original_filename;
                        a.click();
                        URL.revokeObjectURL(url);
                      });
                  }}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download
                </button>
                <button
                  className="file-meta-btn"
                  onClick={() => setForwardingFile(selectedFile)}
                >
                  <CornerUpRight size={14} />
                  Forward
                </button>
              </div>
            </div>
            <div className="file-preview-content">
              {selectedFile.mimetype?.startsWith("image/") ? (
                <img
                  src={getFileUrl(selectedFile.id)}
                  alt={selectedFile.original_filename}
                  className="file-preview-image"
                />
              ) : selectedFile.mimetype?.startsWith("video/") ? (
                <video
                  src={getFileUrl(selectedFile.id)}
                  controls
                  className="file-preview-video"
                />
              ) : selectedFile.mimetype?.startsWith("audio/") ? (
                <div className="file-preview-audio-wrap">
                  <div className="audio-player-card">
                    <div className="audio-player-icon">
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                      >
                        <path d="M9 18V5l12-2v13" />
                        <circle cx="6" cy="18" r="3" />
                        <circle cx="18" cy="16" r="3" />
                      </svg>
                    </div>
                    <div className="audio-player-info">
                      <span className="audio-player-name">
                        {selectedFile.original_filename}
                      </span>
                      <span className="audio-player-meta">
                        {selectedFile.uploader_name || "Unknown"}
                      </span>
                    </div>
                  </div>
                  <audio
                    key={selectedFile.id}
                    src={getFileUrl(selectedFile.id)}
                    controls
                    className="audio-player-controls"
                  />
                </div>
              ) : selectedFile.mimetype === "application/pdf" ? (
                <Tip label={selectedFile.original_filename}>
                  <iframe
                    src={getFileUrl(selectedFile.id)}
                    className="file-preview-pdf"
                  />
                </Tip>
              ) : selectedFile.mimetype === "text/markdown" ||
                selectedFile.original_filename?.endsWith(".md") ? (
                <MarkdownPreview url={getFileUrl(selectedFile.id)} />
              ) : selectedFile.mimetype?.startsWith("text/") ? (
                <MarkdownPreview url={getFileUrl(selectedFile.id)} />
              ) : (
                <div className="file-preview-generic">
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    style={{ color: "var(--text-muted)" }}
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  <span className="file-preview-filename">
                    {selectedFile.original_filename}
                  </span>
                  <span className="file-preview-meta">
                    {selectedFile.mimetype || "Unknown type"}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          color: "var(--text-muted)",
          fontSize: "var(--ofself-text-xl)",
        }}
      >
        Select a channel to start chatting.
      </div>
    );
  }

  /* THE APP'S RAIL (<Shell navItems>, under the mark): the list's toggle, inert on the atrium (it has no list); New,
     what you can start from anywhere; the pages as doors with their counts. The kit's seats stand at the foot
     (RAIL_ITEMS). Focus slides both away. */
  // HOME is a group's atrium (WorldsAtrium stands the last group you were
  // in when Personal has none); with no groups at all, your messages
  const goHome = () => handlePageChange(groups?.length ? "overview" : "home");
  // a compose stands in the conversation's place, which the atrium covers
  const compose = (start) => () => {
    if (activePage === "overview") handlePageChange("home");
    start();
  };
  const canCall = channelTools.some((t) => t.key === "call");
  const navItems = [
    {
      id: "new",
      label: "New",
      object: <NewObject />,
      qhBody: "Starts a conversation: a message to anyone, a channel in this group, or a huddle in the conversation you are in.",
      items: [
        { label: "New message", icon: <MessageCircleIcon size={15} />, onSelect: compose(() => startComposeDM()) },
        activeGroup && { label: "New channel", icon: <Hash size={15} />, onSelect: compose(() => startComposeChannel(activeGroup.id)) },
        { label: "New huddle", icon: <VideoIcon size={15} />, disabled: !canCall || !!activeHuddle, onSelect: startHuddle },
      ].filter(Boolean),
    },
    ...SY_PAGES.map((pg) => ({
      ...pg,
      badge: countBadge({ home: totalUnread, activity: newActivityCount }[pg.id]),
      active: activePage === pg.id,
      onClick: () => handlePageChange(pg.id),
    })),
  ];

  /* THE CONVERSATION'S RIGHT RAIL, SEAT FOR SEAT THE MAC'S (mac/Views/
     Thread.swift ConversationRail, in the kit ChatRail's order): Focus leads,
     ⋯ under it; then the call, the members with their count, the bell; at the
     foot what you hand the conversation: its form (Format), an emoji, a
     mention, an audio or a video clip, a file (Attach), your voice (Voice, the
     kit's useDictation). The composer keeps only the words and opens its doors
     to these (MessageInput `doors`); a drop or a paste still attaches. With no conversation open the rail is bare: Focus alone at its
     block's place. Desktop only; the phone header keeps its own cells. */
  const oneToOne = showMainChat && activeChannel.is_dm && !activeChannel.is_group_dm;
  const memberCount = activeChannelMembers.length;
  const channelTitle = activeChannel?.is_dm ? dmDisplayName(activeChannel) : `# ${activeChannel?.name}`;
  const convoSeats = showMainChat
    ? [
        // WHAT THE ⋯ HELD STANDS ON THE RAIL (owner call 2026-10-30: "no need for the ellipsis menu"):
        // its properties first, what you do to it from anywhere after the bell
        {
          id: "properties",
          label: "Properties",
          object: <PropertiesObject />,
          active: channelPropsOpen,
          onClick: () => setChannelPropsOpen((v) => !v),
          qhBody: "Shows this conversation's properties: its name, topic and who is in it.",
        },
        ...channelTools.map((t) => ({
          id: t.key,
          label: "Call",
          tip: t.label,
          object: <CallObject />,
          active: !!activeHuddle,
          onClick: t.onClick,
        })),
        ...(oneToOne
          ? []
          : [{
              id: "members",
              label: "Members",
              tip: `${memberCount} members`,
              object: <PeopleObject />,
              badge: memberCount > 0 ? memberCount : undefined,
              active: showMembers,
              onClick: handleMembersToggle,
              qhBody: `${memberCount} members. Opens the member list, where you can find, add or message them.`,
            }]),
        {
          id: "mute",
          label: channelMuted ? "Unmute" : "Mute",
          object: <BellObject off={channelMuted} />,
          active: channelMuted,
          onClick: () => toggleChannelMute(activeChannel.id),
          qhBody: channelMuted
            ? "This conversation is muted. Press to let its notifications through again."
            : "Silences notifications from this conversation. Press again to let them through.",
        },
        {
          id: "pin",
          label: pinnedChannels.has(activeChannel.id) ? "Unpin" : "Pin",
          object: <PinObject />,
          active: pinnedChannels.has(activeChannel.id),
          onClick: () => toggleChannelPin(activeChannel.id),
          qhBody: pinnedChannels.has(activeChannel.id) ? "Unpins this conversation from the top of your list." : "Pins this conversation to the top of your list.",
        },
        {
          id: "unread",
          label: "Mark as unread",
          object: <UnreadObject />,
          onClick: () => markAsUnread(activeChannel.id, null),
          qhBody: "Marks this conversation unread, so it waits for you in your list.",
        },
        activeChannel.is_dm
          ? { id: "leave", label: "Delete conversation", object: <TrashObject />, danger: true, onClick: () => leaveChannel(activeChannel.id), qhBody: "Deletes this conversation from your list." }
          : { id: "leave", label: "Leave channel", object: <LeaveObject />, onClick: () => leaveChannel(activeChannel.id), qhBody: "Leaves this channel; you can join it again from the directory." },
        // THE +'S ACTIONS ON THE RAIL (owner call 2026-10-30), above Attach, the composer's doors as Attach is
        ...[
          { id: "format", label: "Format", object: <FormatObject />, door: "format", qhBody: "Shows the formatting bar above the message: bold, italic, links, lists and code." },
          { id: "emoji", label: "Emoji", object: <EmojiObject />, door: "emoji", qhBody: "Opens the emoji picker over the message; the one you pick lands at the cursor." },
          { id: "mention", label: "Mention", object: <MentionObject />, door: "mention", qhBody: "Starts an @mention in the message and lists who you can mention." },
          { id: "audio", label: "Audio clip", object: <ClipObject />, door: "audio", qhBody: "Records an audio clip to send in the conversation." },
          { id: "video", label: "Video clip", object: <FilmObject />, door: "video", qhBody: "Records a video clip from your camera or screen to send in the conversation." },
        ].map(({ door, ...seat }) => ({ ...seat, foot: true, onClick: () => composerDoors.current?.[door]() })),
        {
          id: "attach",
          label: "Attach",
          tip: "Attach a photo or file (or drop or paste one)",
          object: <AttachObject />,
          foot: true,
          onClick: () => composerDoors.current?.attach(),
          qhBody: "Adds a photo or a file to your next message. Dropping or pasting one does the same.",
        },
        {
          id: "voice",
          label: voice.state === "recording" ? "Stop" : voice.state === "transcribing" ? "Writing" : "Voice",
          tip: voice.state === "recording" ? "Stop dictating" : "Dictate into the message",
          object: <VoiceObject />,
          foot: true,
          active: voice.state === "recording",
          disabled: voice.state === "transcribing" || (voice.state === "idle" && !voice.available),
          onClick: voice.toggle,
          qhBody: voice.state === "recording"
            ? "Stops dictation; what you said stays in the message to edit or send."
            : "Dictates into the message as you speak. Press again to stop.",
        },
      ]
    : [];
  const focusWord = focusMode ? "Leave focus" : "Focus";
  const endRail = slideMobile ? null : (
    <>
      <EndRail
        label="Conversation"
        bare={!showMainChat}
        items={[
          {
            id: "focus",
            label: focusWord,
            tip: `${focusWord}  ${formatHotkey(SYNAPSE_HOTKEYS.focus)}`,
            object: <FocusObject />,
            active: focusMode,
            onClick: () => setFocusMode((v) => !v),
          },
          ...convoSeats,
        ]}
      />
      {showMainChat && (
            <PropertiesPlate
              /* NO SEAT OF ITS OWN: the conversation's rail seats Properties
                 (owner call 2026-10-30), so the plate opens from there alone. */
              seat={false}
              open={channelPropsOpen && !!activeChannel}
              onOpenChange={setChannelPropsOpen}
              label="Channel properties"
              className="sy-props-plate"
              title={activeChannel && (activeChannel.is_dm ? dmDisplayName(activeChannel) : `#${activeChannel.name}`)}
              onTitleClick={() => { setChannelDetailTab("about"); setShowMembers(true); }}
            >
              {activeChannel && (
                <ChannelProperties
                  className="channel-meta"
                  channel={activeChannel}
                  members={activeChannelMembers}
                  lastMessageStamp={lastMessageStamp}
                  avatarColor={hashColor}
                  formatTime={formatTime}
                />
              )}
            </PropertiesPlate>
      )}
    </>
  );

  return (
    <>
      {endRail}
      {/* COLD-LOAD INTRO + APP-HOP OUTRO — on load (refresh / arriving authed), the bird
          draws onto the field then fades to the app; on clicking another app in the launcher,
          the bird UNDRAWS (reverse) before navigating, so the next app draws its own. The mark
          is bare: the Splash injects the animation + direction + matching duration. The pre-login
          LoginGate has its own hero draw, so the splash rides the authed path. */}
      {/* Timing rides the shell's snappy defaults — no per-app override. */}
      <Splash mark={<AthleticSwift />} />
      <ShellProvider
        value={{
          currentUser: currentUser
            ? {
                ...currentUser,
                avatarUrl: buildAvatarUrl(
                  userAvatars[currentUser.id] ??
                    currentUser.avatarUrl ??
                    currentUser.avatar_url,
                ),
              }
            : null,
          ...spaces,
          onSwitchSpace: switchToSpace,
          onConnectSpace: connectToGroup,
          spaceUnreads,
          hasOtherSpaceUnreads,
          appUnreads: { synapse: totalUnread },
          notificationInbox: {
            unreadCount: totalUnread,
            onOpen: () => handlePageChange("home"),
          },
          ep: epStatus?.ep,
          groupsAuth: epStatus?.groups_auth,
          // Identity menu: "Log out" ends the Synapse session; "Log out of
          // ecosystem" ends every ofself session. Both relay to Paradigm.
          onSignOut: () => logout(false),
          onSignOutEcosystem: () => logout(true),
        }}
      >
        <Shell
          currentApp="synapse"
          /* THE RAILS (RAIL_ITEMS and navItems above): the kit seats and names them, and on a phone the menu reads
           the same seats. Focus mode slides them away, so they stay mounted. */
          rail
          railItems={RAIL_ITEMS}
          navItems={navItems}
          homeWord="Atrium"
          onHome={goHome}
          /* the list's ⌘\ (its toggle stands in the SplitView's corner) */
          sidebar={{ open: sidebarOpen, onToggle: toggleSidebar, word: "conversations", disabled: activePage === "overview" }}
          dock
          focus={focusMode}
          onFocusChange={setFocusMode}
          /* Synapse's own swift: the rail's head and the field's emblem */
          mark={<AthleticSwift />}
          mobileBreakpoint={mobileBp}
          /* ONE mobile signal: the same boolean that drives the panel slide track,
           so the rail-fold and the slider flip together (no desync, no white-space). */
          mobile={slideMobile}
          /* THE COMMAND DOCK — the bottom panel (⌘J): commands + transcript.
           Synapse keeps no separate agent chat, so a line the dock can't
           parse has nowhere to fall through to. */
          commands={dockCommands}
          /* the rail's Search seat — the same spotlight. toggle (not open) so
           a second press on the seat dismisses it. */
          onSearch={() => topBarRef.current?.toggle()}
          /* Synapse's settings PANES, mounted in the shell's window (two
           levels: System above, Synapse below). SettingsDetail is the same
           lazy chunk the old in-app modal used. */
          settings={[
            /* (the Profile pane retired 2026-08-23, owner call.) It seated a
             SYSTEM-level pane — the Paradigm identity, one profile across the
             ecosystem — inside a single app's settings window, so synapse was
             answering for something it does not own. Nothing else read it; the
             avatar upload it carried lives on the identity itself. */
            {
              id: "synapse-appearance",
              label: "Appearance",
              icon: (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="5" />
                  <line x1="12" y1="1" x2="12" y2="3" />
                  <line x1="12" y1="21" x2="12" y2="23" />
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                  <line x1="1" y1="12" x2="3" y2="12" />
                  <line x1="21" y1="12" x2="23" y2="12" />
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                </svg>
              ),
              content: (
                <Suspense fallback={null}>
                  <SettingsDetail
                    category="appearance"
                  />
                </Suspense>
              ),
            },
            {
              id: "synapse-privacy",
              label: "Privacy",
              icon: (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              ),
              content: (
                <Suspense fallback={null}>
                  <SettingsDetail
                    category="privacy"
                  />
                </Suspense>
              ),
            },
          ]}
        >
          <div className="app-main-column">
            {/* Universal search — the kit Spotlight, opened from the sidebar Search tab */}
            <SearchSpotlight ref={topBarRef} />

            {/* THE WORKSPACE: the list and the conversation (and the thread),
              beside the rail. The pages are the rail's doors (the Mac's),
              so no page column stands between the rail and the list. */}
            <SplitView
              /* THE LIST'S TOGGLE STANDS IN THE CORNER, AFTER THE STEPS (as Caliber's and Ink's do, the kit's
                 HistorySteps); the atrium has no list to toggle */
              corner={<HistorySteps {...historySteps} sidebar={activePage === "overview" ? undefined : { open: sidebarOpen, onToggle: toggleSidebar, word: "conversations" }} />}
              className="content-card"
              data-qh-title="Workspace"
              data-qh-body="Your workspace — the sidebar, conversation, and thread panels are arranged here."
              /* THE LIST STANDS OR IS GONE (the Mac's hasSide): the rail's
                 sidebar seat closes it outright (width 0, kit-eased), as do
                 the atrium (a whole surface, with no list) and focus mode.
                 The phone's slide track always keeps its leading pane. */
              leadingOpen={slideMobile ? true : activePage !== "overview" && !focusMode && sidebarOpen}
              {...mobilePanelsProps}
              /* on a phone the kit's SplitView pages the list and the
                 conversation itself, from this index, and stands the way
                 back (audit 2026-10-02: without these it stayed on the list) */
              mobileIndex={mobilePanelIndex}
              onMobileBack={goBackPanel}
              leadingRef={sidebarResizableRef}
              leadingProps={{
                "data-dragging": isDragging || undefined,
                "data-qh-title": "Sidebar",
                "data-qh-body":
                  "Your channel list, direct messages, and navigation hub for this space.",
              }}
              leadingStyle={
                slideMobile
                  ? undefined
                  : {
                      width: "var(--shell-sidebar-w, 350px)",
                      minWidth: "var(--shell-sidebar-w, 350px)",
                    }
              }
              seamProps={!isMobilePanels ? sidebarResizeProps : undefined}
              leadingClassName="unirail"
              leading={
                <div className="sy-leading-row">
                  <div className="unirail-main ofself-wing-clear">
                  {/* No header band on desktop — the list starts at the top
                  edge (the compose foot is the sidebar's one band). Mobile
                  keeps the band for the back chevron, which opens the
                  full-screen railbar page, the level ABOVE the app. */}
                  {isMobilePanels && (
                    <BlockTitle
                      leading={
                        <NavMenuButton
                          variant="back"
                          className="unirail__menu"
                        />
                      }
                    />
                  )}
                  {/* Keyed by activePage so switching Messages / Activity / DMs / etc.
                  remounts this wrapper and replays the fade. flex:1 + min-height:0
                  makes it occupy the same space the bare panel did (the page's
                  `.unirail .sidebar { flex:1 }` still matches through the wrapper). */}
                  <div key={activePage} className="block-fade ofself-page-fade">
                    {renderSidePanel()}
                  </div>
                  {/* (the COMPOSE foot retired 2026-08-17; its verbs are
                  the rail's New now, as the Mac's) */}
                  </div>
                  {/* The SHELL's resize handle — an absolute hit-zone straddling sidebar's
                  right seam (.block__resize--right), exactly like every panel in the
                  wireframe. No flex-child handle, no gutter margins: the .blocks
                  gap:0 keeps the panels flush; --seam-line draws the hairline. */}
                  {/* the seam stays through COLLAPSED too — drag the bar back
                  open, the rail's own gesture (kit ≥0.19.0) */}
                  {/* the in-module DIVIDER between the list and the conversation — a free-px drag,
                  the same seam classic uses. Lives in BOTH modes now: it's the internal seam. */}
                  <div
                    className="block__resize block__resize--right"
                    {...sidebarResizeProps}
                    onContextMenu={(e) => e.preventDefault()}
                  />
                </div>
              }
              trailing={
                <>
                  {/* Panel 2 — the content/chat area (the fill panel) */}
                  <main
                    ref={chatMainRef}
                    /* ofself-page-fade: the kit's page-arrival rise — this block mounts
                       fresh when a conversation page replaces the overview, so the
                       fade replays on page arrival and never on channel switches
                       (the block stays mounted through those). */
                    className={`block conversation block--fill ofself-desk ofself-page-fade${isDragging ? " conversation-dragging" : ""}`}
                    data-qh-title="Conversation"
                    data-qh-body="Messages in the current channel or direct message. Scroll up to load older messages."
                  >
                    {/* No card label here — the REAL channel header (avatar + name + actions) is the title,
                  un-hidden in canvas now. (The tile's move handle + full-screen button live on the
                  sidebar's title bar, top-left of the tile.) */}
                    {/* The THREAD seam's single handle — one widened, grip-bearing hit-zone on the
                  conversation's right edge (the sidebar seam needs no conversation mirror: its
                  --seam handle on the sidebar's own edge already spans the gutter unclipped). */}
                    {effectiveShowThread && (
                      <div
                        className="block__resize block__resize--fill-right"
                        {...threadResizeProps}
                        data-resize-edge="right"
                        aria-hidden="true"
                        tabIndex={-1}
                      />
                    )}
                    {activePage === "overview" ? (
                      /* HOME IS THE ATRIUM (owner call 2026-10-02): the group's
                         worlds at once, behind the column's Sy tile. It takes the
                         whole surface; a press steps into a world's channels. */
                      <WorldsAtrium onEnter={() => handlePageChange("home")} />
                    ) : (
                      /* THE ROOM: the world's channels settle in as the
                         atrium's pieces fall away (the shell's ripple) */
                      <RoomArrival id={activeWorld}>
                      {composingDM ? (
                      <ComposeDirectMessage
                        createDM={createDM}
                        createPluginDM={createPluginDM}
                        cancelComposeDM={cancelComposeDM}
                        currentUser={currentUser}
                        members={members}
                        dmChannels={dmChannels}
                        selectChannel={selectChannel}
                        sendMessage={sendMessage}
                      />
                    ) : composingChannel ? (
                      <ComposeChannel
                        groupId={composingChannel}
                        groups={groups}
                        groupChannelsByGroup={groupChannelsByGroup}
                        createGroupChannel={createGroupChannel}
                        cancelComposeChannel={cancelComposeChannel}
                        currentUser={currentUser}
                      />
                    ) : showMainChat ? (
                      <>
                        <header
                          className="channel-header block__title"
                          key={activeChannel?.id}
                          data-qh-title="Channel header"
                          data-qh-body="Shows the current channel name and actions — members, video call, and search within this channel."
                        >
                          {/* Layout-sized inner that carries the glass lens (mirrors the
                        composer's .composer-shell) — keeps the refraction crisp on resize.
                        .block__title-surface is the shared slot-level glass target. */}
                          <div className="channel-header-inner block__title-surface">
                            {/* Mobile: chevron-back to the sidebar — the same stylized
                        button as the thread close. */}
                            <HeaderBack
                              show={canGoBackPanel}
                              onBack={goBackPanel}
                            />
                            <div
                              className={`channel-header-left${callIslandState.mode ? ` call-island-${callIslandState.mode}` : ""}`}
                              onClick={
                                callIslandState.mode
                                  ? undefined
                                  : () => {
                                      setChannelDetailTab("about");
                                      setShowMembers(true);
                                    }
                              }
                              style={{
                                cursor: callIslandState.mode
                                  ? "default"
                                  : "pointer",
                              }}
                            >
                              {callIslandState.mode ? (
                                <CallIsland
                                  channel={activeChannel}
                                  mode={callIslandState.mode}
                                  huddle={callIslandState.huddle}
                                  currentUser={currentUser}
                                />
                              ) : activeChannel.is_dm ? (
                                <ConvoAvatar
                                  convo={activeChannel}
                                  name={dmDisplayName(activeChannel)}
                                  className="channel-header-avatar"
                                  size={28}
                                />
                              ) : null /* the hash disc retired (owner call
                                2026-08-16) — the # rides IN the name text */}
                              {!callIslandState.mode && (
                                <h2 className="channel-header-name">
                                  {activeChannel.is_dm
                                    ? dmDisplayName(
                                        activeChannel,
                                        "Direct Message",
                                      )
                                    : `#${activeChannel.name}`}
                                </h2>
                              )}
                            </div>
                            <div className="channel-header-right block__title-actions">
                              <TipProvider>
                                {/* the bar's stack is MOBILE-ONLY again
                                    (owner call 2026-08-16, the last word):
                                    on desktop the members stack is a
                                    PROPERTY in the header ledger below —
                                    the bar copy was redundant. Mobile has
                                    no ledger, so it keeps the stack. */}
                                {isMobilePanels &&
                                  !(
                                    activeChannel.is_dm &&
                                    !activeChannel.is_group_dm
                                  ) &&
                                  activeChannelMembers.length > 0 && (
                                    <Tip side="bottom" label={`${activeChannelMembers.length} members`}>
                                      <button
                                        className="header-members-stack"
                                        aria-label={`${activeChannelMembers.length} members`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setShowMembers(true);
                                        }}
                                      >
                                        {activeChannelMembers
                                          .slice(0, 4)
                                          .map((m, i) => {
                                            const name = m.username || "?";
                                            const avColor = hashColor(name);
                                            return (
                                              <span
                                                key={m.user_id}
                                                className="header-member-avatar"
                                                style={{
                                                  "--av-color": avColor,
                                                  zIndex: 4 - i,
                                                }}
                                              >
                                                {name[0].toUpperCase()}
                                              </span>
                                            );
                                          })}
                                        {activeChannelMembers.length > 4 && (
                                          <span className="header-member-overflow">
                                            +{activeChannelMembers.length - 4}
                                          </span>
                                        )}
                                      </button>
                                    </Tip>
                                  )}
                                {/* THE ACTIONS ARE HEADER CELLS AGAIN (owner
                                call 2026-08-16): the channel ToolRail retired —
                                every width keeps the header's own cells. */}
                                {channelTools.map((t) => (
                                    <Tip key={t.key} side="bottom" label={t.label}>
                                      <button
                                        className={`block__title-act${t.className ? ` ${t.className}` : ""}`}
                                        aria-label={t.label}
                                        onClick={t.onClick}
                                      >
                                        {t.icon}
                                      </button>
                                    </Tip>
                                  ))}
                                {/* (the header's fullscreen cell retired
                                2026-08-16 — ONE fullscreen, the corner's,
                                wired to this same focusMode) */}
                                <div className="header-more-wrap">
                                  {bulkEditMode ? (
                                    <button
                                      className="header-action header-action-active"
                                      onClick={() => {
                                        setBulkEditMode(false);
                                        setBulkSelectedMessages(new Set());
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: "var(--ofself-text-base)",
                                          fontWeight:
                                            "var(--ofself-weight-semibold)",
                                        }}
                                      >
                                        Done
                                      </span>
                                    </button>
                                  ) : (
                                    <Menu
                                      tooltip="More"
                                      trigger={
                                        <button
                                          className="block__title-act"
                                          aria-label="More"
                                        >
                                          <svg
                                            width="18"
                                            height="18"
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                          >
                                            <circle cx="12" cy="5" r="2" />
                                            <circle cx="12" cy="12" r="2" />
                                            <circle cx="12" cy="19" r="2" />
                                          </svg>
                                        </button>
                                      }
                                      items={[
                                        {
                                          label: "Edit",
                                          icon: (
                                            <svg
                                              width="15"
                                              height="15"
                                              viewBox="0 0 24 24"
                                              fill="none"
                                              stroke="currentColor"
                                              strokeWidth="2"
                                            >
                                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                            </svg>
                                          ),
                                          onSelect: () => setBulkEditMode(true),
                                        },
                                      ]}
                                    />
                                  )}
                                </div>
                              </TipProvider>
                            </div>
                          </div>
                        </header>
                        {/* THE HEADER GROWS INK'S BLOCK (owner call
                            2026-08-16, said twice — the literal header, not
                            an in-scroll intro): the channel's name LARGE,
                            the properties ledger percolating below — members
                            (the stack, a door to the detail), started, last
                            message — fixed above the scroll like ink's note
                            head. The compact bar above keeps the actions;
                            its centered name yields to this one (CSS). */}
                        {/* (the channel-props band retired 2026-08-22 —
                            its name + ledger live in the corner's
                            properties plate now; the sidebar already says
                            where you are) */}
                        {/* MessageList's root IS the shell body (.block__body),
                      directly inside the panel — no .message-list-card wrapper
                      (that was the dead thread-sleeve's flex-row container). */}
                        <MessageList />
                        {composerFoot}
                      </>
                    ) : activePage === "directory" ? (
                      <div className="conversation-inner">
                        <DirectoryDetail
                          member={selectedDirMember}
                          onStartDM={() => handlePageChange("dms")}
                          onNavigateChannel={() => handlePageChange("home")}
                        />
                      </div>
                    ) : activePage === "files" && selectedFile ? (
                      renderMainContent()
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          color: "var(--text-muted)",
                          fontSize: "var(--ofself-text-xl)",
                        }}
                      >
                        Select a channel to start chatting.
                      </div>
                    )}
                      </RoomArrival>
                    )}
                  </main>

                  {/* The thread — the third in-module pane. By the litmus test a thread doesn't
                exist on its own → it lives INSIDE the app tile (classic flush pane with the
                free-px divider), opening squeezes the conversation. */}
                  <ThreadDock
                    open={effectiveShowThread}
                    basis={threadWidth}
                    separatorProps={threadResizeProps}
                    isMobile={slideMobile}
                  />

                  {/* Recordings — the thread's twin. Only MOUNTED when there's actually a
                recordings huddle, so it can never take width / push the layout when
                closed. (Opening a thread clears recordingsHuddleId, so they're
                mutually exclusive in the right-hand slot.) */}
                  {recordingsHuddleId && (
                    <RecordingsDock
                      open
                      huddleId={recordingsHuddleId}
                      groupId={activeChannel?.group_id}
                      maxWidth={() =>
                        Math.max(
                          THREAD_MIN,
                          containerWidthForResize() - sidebarWidth - CHAT_MIN,
                        )
                      }
                    />
                  )}
                  {!isMobilePanels && effectiveShowThread && (
                    <SeamGrip
                      anchorRef={chatMainRef}
                      edge="right"
                      separatorProps={threadResizeProps}
                    />
                  )}

                </>
              }
            />

            {/* THE CHANNEL'S DETAIL: the shell's two-part sheet (Modal size="tall"), its sections the shell's tabs */}
            <Modal
              open={showMembers}
              onClose={closeMembers}
              title={channelTitle}
              size="tall"
              className="channel-detail"
              onOpenAutoFocus={(e) => e.preventDefault()}
            >
              <GridNav
                aria-label="Channel detail sections"
                items={[
                  { id: "about", label: "About" },
                  { id: "members", label: "Members", count: activeChannelMembers.length },
                  { id: "files", label: "Files" },
                  { id: "settings", label: "Settings" },
                ]}
                active={channelDetailTab}
                onSelect={setChannelDetailTab}
              >
                      <div className="channel-detail-content">
                        {channelDetailTab === "about" && (
                          <div className="channel-detail-about">
                            {/* THE CHANNEL'S PROPERTIES (owner call 2026-08-16,
                                ink's entry-meta grammar — marked for the shell
                                uproot): members as the avatar stack (a door to
                                the members tab), started, last message. */}
                            {/* The SAME ledger as the channel's header — one
                                component, one stored record, so the two
                                surfaces cannot drift. */}
                            <ChannelProperties
                              className="channel-meta"
                              channel={activeChannel}
                              members={activeChannelMembers}
                              lastMessageStamp={lastMessageStamp}
                              avatarColor={hashColor}
                              formatTime={formatTime}
                            />
                            <h4 className="channel-detail-section-title ofself-label">
                              Channel name
                            </h4>
                            <p className="channel-detail-value">
                              {activeChannel?.is_dm
                                ? dmDisplayName(activeChannel)
                                : activeChannel?.name}
                            </p>
                            {activeChannel?.description && (
                              <>
                                <h4 className="channel-detail-section-title ofself-label">
                                  Description
                                </h4>
                                <p className="channel-detail-value">
                                  {activeChannel.description}
                                </p>
                              </>
                            )}
                            {activeChannel?.group_name && (
                              <>
                                <h4 className="channel-detail-section-title ofself-label">
                                  Group
                                </h4>
                                <p className="channel-detail-value">
                                  {activeChannel.group_name}
                                </p>
                              </>
                            )}
                            <h4 className="channel-detail-section-title ofself-label">
                              Type
                            </h4>
                            <p className="channel-detail-value">
                              {activeChannel?.type === "private"
                                ? "Private channel"
                                : activeChannel?.is_dm
                                  ? "Direct message"
                                  : "Public channel"}
                            </p>
                          </div>
                        )}
                        {channelDetailTab === "members" && (
                          <MembersTab
                            members={activeChannelMembers}
                            currentUser={currentUser}
                            createDM={createDM}
                            closeModal={closeMembers}
                            dmChannels={dmChannels}
                            selectChannel={selectChannel}
                            groupMembers={groupMembers}
                            addChannelMember={addChannelMember}
                            activeChannel={activeChannel}
                          />
                        )}
                        {channelDetailTab === "files" && (
                          <div className="channel-detail-files">
                            <FilesPage />
                          </div>
                        )}
                        {channelDetailTab === "settings" && (
                          <div className="channel-detail-about">
                            <h4 className="channel-detail-section-title ofself-label">
                              Notifications
                            </h4>
                            <p
                              className="channel-detail-value"
                              style={{ color: "var(--text-muted)" }}
                            >
                              Default (all messages)
                            </p>
                            <h4 className="channel-detail-section-title ofself-label">
                              Channel ID
                            </h4>
                            <p
                              className="channel-detail-value"
                              style={{
                                fontFamily: "var(--font-mono)",
                                fontSize: "var(--ofself-text-base)",
                                color: "var(--text-muted)",
                              }}
                            >
                              {activeChannel?.id}
                            </p>
                            <div
                              style={{
                                marginTop: 24,
                                display: "flex",
                                flexDirection: "column",
                                gap: 8,
                              }}
                            >
                              <button
                                className="channel-detail-danger-btn"
                                onClick={() => {
                                  closeMembers();
                                }}
                              >
                                Clear Chat History
                              </button>
                              <button
                                className="channel-detail-danger-btn"
                                onClick={() => {
                                  if (
                                    confirm(
                                      `${activeChannel?.is_dm ? "Delete this conversation" : "Leave this channel"}? This cannot be undone.`,
                                    )
                                  ) {
                                    closeMembers();
                                    if (activeChannel?.is_dm) {
                                      deleteChannel(activeChannel.id);
                                    } else {
                                      leaveChannel(activeChannel.id);
                                    }
                                  }
                                }}
                              >
                                {activeChannel?.is_dm
                                  ? "Delete Chat"
                                  : "Leave Channel"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
              </GridNav>
            </Modal>
          </div>

          {/* Settings moved into the SHELL's window (rail cog → ShellSettings):
            Synapse passes its panes via the `settings` prop on Shell. */}

          <IncomingCallPanel />
          {forwardingFile && (
            <FileForwardModal
              file={forwardingFile}
              channels={channels}
              dmChannels={dmChannels}
              groups={groups}
              groupChannelsByGroup={groupChannelsByGroup}
              onForward={async (targetId) => {
                const content = `> **Shared file: ${forwardingFile.original_filename}**`;
                await forwardMessage(targetId, {
                  content,
                  file_id: forwardingFile.id,
                  sender: { username: currentUser?.username || "Unknown" },
                });
                setForwardingFile(null);
              }}
              onClose={() => setForwardingFile(null)}
            />
          )}
          {/* THE HUDDLE OVERLAY — a top-level, always-mounted fullscreen/pip/bar portal (it self-hides
            when you're not in a huddle, HuddleBar:945). Lives HERE, not inside the fused-app tile, so a
            huddle started from ANY surface — the modular Chat module, the fused app, the Huddles page —
            shows its fullscreen view + minimize. (Previously it was nested in the fused tile, which the
            modular canvas board never renders, so starting a huddle there did nothing.) */}
          <HuddleBar />
          {/* (the nucleus shelf moved into the workspace row as a kit
            <SlideOutPanel>, beside the agent — see the AgentPanel above. Its
            wire is unchanged: the app's Bearer transport, authedFetch →
            /api/chat/nucleus/*, rows Synapse can resolve opening that
            conversation and everything else peeking in place.) */}
        </Shell>
      </ShellProvider>
    </>
  );
}
